import { Injectable } from '@angular/core';
import {
  CostReviewBidDocumentationAvailable, CostReviewBuildingLevelNetworkPanels, CostReviewChillerPlant,
  CostReviewContractualDocumentation, CostReviewDampers, CostReviewElectricalWiring, CostReviewGeneralConditions,
  CostReviewHeatingPlant, CostReviewMarginExceptions, CostReviewOperatorWorkstationSoftware,
  CostReviewPlannedCosts, CostReviewPrefab, CostReviewProjectMgmtRequirements, CostReviewSmokeDampers, CostReviewSummary,
  CostReviewTerminalControllers, CostReviewFireEnUsSystemAttributes, CostReviewFireEnUsSystemConfiguration,
  CostReviewFireEnUsSystemInterfaces, CostReviewWiringInterlocks, CostReviewFireEnUsExecutionSchedule,
  CostReviewFireEnUsOutsidePurchases, CostReviewFireEnUsRetrofitConsiderations, CostReviewFln, CostReviewMetering,
  CostReviewIntegration, CostReviewAirHandlers, CostReviewHumidifiers, CostReviewExhaustFans, CostReviewAfms,
  CostReviewControlValves, CostReviewVfds, CostReviewLabEquipment, CostReviewAttributes, CostReviewFireEnUsAttributes,
  CostReviewSecurityEnUsAttributes,
  CostReviewSecurityEnUsGeneralConditions,
  CostReviewSecurityEnUsExecutionSchedule,
  CostReviewSecurityEnUsNetwork,
  CostReviewSecurityEnUsRetrofitConsiderations,
  CostReviewSecurityEnUsAccessControlSystems,
  CostReviewSecurityEnUsCctvSystems,
  CostReviewSecurityEnUsIntercomSystem,
  CostReviewSecurityEnUsIntrusionSystem,
  CostReviewFireEnUsWiringResponsibilities,
  CostReviewSecurityEnUsManagementStation,
  CostReviewElectricalAttributes,
  CostReviewElectricalComments,
  CostReviewElectricalDocumentation,
  CostReviewElectricalEquipment,
  CostReviewElectricalLaborAndMaterials,
  CostReviewElectricalProvidedDocuments,
  CostReviewElectricalSafety,
  CostReviewElectricalScopeOfWork,
  CostReviewElectricalSiteAccessibility,
  CostReviewElectricalSubcontractors,
  CostReviewBaseAttributes,
  CostReviewStatus,
  CostReviewerDetails,
  ReviewerTypeItem,
  CostReviewDatabaseRecord,
  TaskBasedLaborSettings
} from '../../../models/dtos';
import { CommonDataService } from '../../common-data.service';
import { CostReviewParameters } from '../../../models/common/CostReviewParameter';
import { IReportData } from '../../../models/reports/IReportData';
import { TranslateService } from '@ngx-translate/core';
import { CommonFunctionsService } from '../../common-functions.service';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { CONSTANTS } from '../../../Constants/Constants';
import { ITableData } from '../../../models/reports/ITableData';
import { IData } from '../../../models/reports/IData';
import { ReportTextAlignment } from '../../../models/reports/ReportTextAlignment';
import { ProjectCategoryVerification, PlannedCostLaborFields, CostReviewType, EstimateType, CULTURE_ID } from '../../../models/enum';
import { DatePipe } from '@angular/common';
import { IFooter } from '../../../models/reports/IFooter';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { ChecklistReportGenericFieldMap } from '../../../models/reports/ChecklistReportGenericFieldMap';
import { CostReviewEstimateType, ReviewDetailTabsService } from 'review-detail-tabs';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { CommonMetaDataService } from '../../common-meta-data.service';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision-pipe/decimal-precision.pipe';
import { take } from 'rxjs';
import { roundToPrecision } from '../../../../types/number-extensions';

type DataValue = string | number | boolean | number[] | Date;
type allTypes = string | number | boolean | Date | null | undefined | number[] | string[];

@Injectable()
export class SolChecklistBaseService {

  reviewData: CostReviewBaseAttributes | null = null;
  reviewParameterData: CostReviewParameters[] = [];
  modifiedReviewParameterData: Map<string, string> = new Map<string, string>();
  checklistTableData: ITableData[] = [];
  business: string = '';
  costReviewType: CostReviewType = CostReviewType.None;
  estimateType: EstimateType = EstimateType.None;
  private readonly FIXED_SECOND_COLUMN_WIDTH: string = '526';
  approvedSellPrice: number = 0;
  costReviewStatus: CostReviewStatus = CostReviewStatus.None;
  costReviewerList: CostReviewerDetails[] = [];
  costReviewerType: ReviewerTypeItem[] = [];
  isReviewTabVisible: boolean = false;

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly translate: TranslateService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly datePipe: DatePipe,
    private readonly reviewDetailsTabService: ReviewDetailTabsService,
    private readonly multilingualCurrencyPipe: MultilingualCurrencyPipe,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe
  ) { }

  generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true) {
    const reportName = this.reportsCommonService.getSelectedReportName();
    this.checkReviewTabVisibility();
    const costReviewDetails: CostReviewDatabaseRecord = this.commonDataService.getCostReviewData() || null;
    this.approvedSellPrice = costReviewDetails?.approvedSellPrice || 0;
    this.reviewData = costReviewDetails?.reviewObject || null;
    this.business = this.commonDataService.getEstimateData()?.business || '';
    this.costReviewerType = this.commonMetaDataService.getMetaData()?.costReviewerTypes || [];
    this.estimateType = this.convertToEstimateType(
      this.reviewDetailsTabService.getOptionalWorkflowAndEstimateType().optionalEstimateType
    );
    this.costReviewStatus = (costReviewDetails?.status as CostReviewStatus) ?? CostReviewStatus.None;
    this.costReviewerList =  costReviewDetails?.costReviewers || [];
    this.reviewParameterData = this.commonDataService.getReviewParameterResponse();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    this.modifyReviewParameterData(this.reviewParameterData);
    let reportData: IReportData;
    reportData = {
      selectionName: reportName,
      reportTitle: this.getReportNameDisplayTitle(reportName),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: this.generateTableData(),
      pageOrientation: pageOrientation,
      internalReport: internalReport
    }
    this.reportsCommonService.setSelectedReport(reportData);
  }

  private getReportNameDisplayTitle(reportName: string): string {
    switch (reportName) {
      case 'CHECKLIST_ELECTRICAL':
        return this.translate.instant('ELECTRICAL_COST_REVIEW_CHECKLIST');
      case 'CHECKLIST_FIRE':
        return this.translate.instant('FIRE_COST_REVIEW_CHECKLIST');
      case 'CHECKLIST_SECURITY_REPORT':
        return this.translate.instant('SECURITY_COST_REVIEW_CHECKLIST');
      case 'CHECKLIST_AUTOMATION':
        return this.translate.instant('AUTOMATION_COST_REVIEW_CHECKLIST');
      case 'CHECKLIST_MECHANICAL':
        return this.translate.instant('MECHANICAL_COST_REVIEW_CHECKLIST');
      default:
        return reportName;
    }
  }

  checkReviewTabVisibility(){
    this.commonDataService.isReviewTabVisible$.pipe(
      take(1)
    ).subscribe((isVisible: boolean) => {
      this.isReviewTabVisible = isVisible;
    });
  }

  private convertToEstimateType(optionalEstimateType: CostReviewEstimateType | undefined): EstimateType {
    switch (optionalEstimateType) {
      case CostReviewEstimateType.EstimateLess50K:
        return EstimateType.EstimateLess50K;
      case CostReviewEstimateType.Estimate50_150K:
        return EstimateType.Estimate50_150K;      
      case CostReviewEstimateType.EstimateGreater150K:
        return EstimateType.EstimateGreater150K;
      default:
        return EstimateType.None;
    }
  }

  modifyReviewParameterData(reviewParameterData: CostReviewParameters[]) {
    reviewParameterData.forEach((param: CostReviewParameters) => {
      this.modifiedReviewParameterData.set(param.parameterID, param.description);
    });
  }


  generateTableData(): ITableData[] {
    this.checklistTableData = [];
    if(!this.isReviewTabVisible) {
      return this.checklistTableData;
    }
    // Logic to identify the report business type then accordingly pass the data to generate the table data
    let costReviewAttributes: CostReviewAttributes | CostReviewElectricalAttributes | CostReviewFireEnUsAttributes | CostReviewSecurityEnUsAttributes | undefined;
    switch (this.business) {
      case CONSTANTS.BUSINESS_AUTOMATION:
      case CONSTANTS.BUSINESS_MECHANICAL:
        this.costReviewType = CostReviewType.AutomationEnUs;
        costReviewAttributes = this.reviewData as CostReviewAttributes;
        break;
      case CONSTANTS.BUSINESS_SECURITY:
        this.costReviewType = CostReviewType.SecurityEnUs;
        costReviewAttributes = this.reviewData as CostReviewSecurityEnUsAttributes;
        break;
      case CONSTANTS.BUSINESS_FIRE:
        this.costReviewType = CostReviewType.FireEnUs;
        costReviewAttributes = this.reviewData as CostReviewFireEnUsAttributes;
        break;
      case CONSTANTS.BUSINESS_ELECTRICAL:
        // Below line is checking if estimate is of new electrical or old electrical where CostReviewAttributes is being used, 
        // if costReviewScopeOfWork is present then its new electrical and we can use CostReviewElectricalAttributes which has more fields specific to electrical estimate  
        if(this.reviewData && 'costReviewScopeOfWork' in this.reviewData){ 
          this.costReviewType = CostReviewType.ElectricalEnUs;
          costReviewAttributes = this.reviewData as CostReviewElectricalAttributes;
        } else {
          this.costReviewType = CostReviewType.AutomationEnUs;
          costReviewAttributes = this.reviewData as CostReviewAttributes;
        }
        break;
      default:    
        costReviewAttributes = undefined;
    }

    if (!costReviewAttributes) {
      return this.checklistTableData;
    }

    this.getRequestDetailsTable();
    this.generateSummaryTable(costReviewAttributes.costReviewSummary);
    this.generatePlannedCostTable(costReviewAttributes as CostReviewAttributes);
    this.generateMarginExceptionsTable(costReviewAttributes.costReviewMarginExceptions, costReviewAttributes.costReviewSummary);
    if(this.costReviewType !== CostReviewType.ElectricalEnUs) {
      const costRiviewData = costReviewAttributes as CostReviewAttributes | CostReviewSecurityEnUsAttributes | CostReviewFireEnUsAttributes
      this.commonFieldsApplicableToAllExceptElectrical(costRiviewData);
    }

    switch (this.costReviewType) {
      case CostReviewType.FireEnUs:
      { const costReviewFire = costReviewAttributes as CostReviewFireEnUsAttributes;
        this.generateSystemAttributesTable(costReviewFire.costReviewFireEnUsSystemAttributes);
        this.generateSystemConfigurationTable(costReviewFire.costReviewFireEnUsSystemConfiguration);
        this.generateRetrofitTableFire(costReviewFire.costReviewFireEnUsRetrofitConsiderations);
        this.generateOperatorWorkstationAndSoftwareTableFire(costReviewFire.costReviewOperatorWorkstationSoftware);
        this.generateElectricalWiringTableFire(costReviewFire.costReviewElectricalWiring, costReviewFire.costReviewFireEnUsWiringResponsibilities);
        this.generateSystemInterfacesTable(costReviewFire.costReviewFireEnUsSystemInterfaces);
        this.generateSystemExecutionScheduleTable(costReviewFire.costReviewFireEnUsExecutionSchedule);
        this.generateOutsidePurchasesTable(costReviewFire.costReviewFireEnUsOutsidePurchases);
        break; 
      }
      case CostReviewType.SecurityEnUs:
     {   const costReviewSecurity = costReviewAttributes as CostReviewSecurityEnUsAttributes;
        this.generateExecutionScheduleTableSecurity(costReviewSecurity.costReviewSecurityEnUsExecutionSchedule);
        this.generateNetworkTableSecurity(costReviewSecurity.costReviewSecurityEnUsNetwork);
        this.generateRetrofitTableSecurity(costReviewSecurity.costReviewSecurityEnUsRetrofitConsiderations);
        this.generateAccessControlTableSecurity(costReviewSecurity.costReviewSecurityEnUsAccessControlSystems);
        this.generateCCTVSystemTableSecurity(costReviewSecurity.costReviewSecurityEnUsCctvSystems);
        this.generateIntercomTableSecurity(costReviewSecurity.costReviewSecurityEnUsIntercomSystem);
        this.generateIntrusionSystemTableSecurity(costReviewSecurity.costReviewSecurityEnUsIntrusionSystem);
        this.generateElectricalWiringTableSecurity(
          costReviewSecurity.costReviewElectricalWiring,
          costReviewSecurity.costReviewFireEnUsWiringResponsibilities
        );
        this.generateManagementTableSecurity(costReviewSecurity.costReviewSecurityEnUsManagementStation);
        break;
      }
      case CostReviewType.AutomationEnUs:
        this.commonAutomationMethodsTableGeneration(costReviewAttributes as CostReviewAttributes);
        break;
      case CostReviewType.ElectricalEnUs:
        this.generateElectricalChecklistTables(costReviewAttributes as CostReviewElectricalAttributes);
        break;
    }

    return this.checklistTableData;
  }

  commonFieldsApplicableToAllExceptElectrical(costReviewAttributes: CostReviewAttributes | CostReviewSecurityEnUsAttributes | CostReviewFireEnUsAttributes) {
    if (this.costReviewType === CostReviewType.SecurityEnUs) {
      this.generateGeneralConditionsTableSecurity((costReviewAttributes as CostReviewSecurityEnUsAttributes).costReviewSecurityEnUsGeneralConditions, costReviewAttributes.costReviewGeneralConditions);
    } else {
      let isCpmVisible = this.approvedSellPrice >  CONSTANTS.ONE_MILLION;
      this.generateGeneralConditionsTable(costReviewAttributes.costReviewGeneralConditions, isCpmVisible);
    }
    if (this.costReviewType === CostReviewType.AutomationEnUs) {
      this.generatePrefabTable((costReviewAttributes as CostReviewAttributes).costReviewPrefab);
    }
    this.generateBidDocumentationAvailableTable(costReviewAttributes.costReviewBidDocumentationAvailable);
    this.generateContractualDocumentationTable(costReviewAttributes.costReviewContractualDocumentation);
    this.generateProjectMgmtRequirementsTable(costReviewAttributes.costReviewProjectMgmtRequirements);
  }

  commonAutomationMethodsTableGeneration(costReviewAttributes: CostReviewAttributes) {
    this.generateBuildingLevelNetworkPanelTable(costReviewAttributes.costReviewBuildingLevelNetworkPanels);
    this.generateOperatorWorkstationAndSoftwareTable(costReviewAttributes.costReviewOperatorWorkstationSoftware);
    this.generateChillarPlantTable(costReviewAttributes.costReviewChillerPlant);
    this.generateHeatingPlantTable(costReviewAttributes.costReviewHeatingPlant);
    if (this.estimateType === EstimateType.EstimateGreater150K) {
      this.generateFLNTable(costReviewAttributes.costReviewFln);
    }
    this.generateTerminalControllerTable(costReviewAttributes.costReviewTerminalControllers);
    if (this.estimateType === EstimateType.EstimateGreater150K) {
      this.generateLabEquipmentTable(costReviewAttributes.costReviewLabEquipment);
      this.generateVFDsTable(costReviewAttributes.costReviewVfds);
      this.generateControlValvesTable(costReviewAttributes.costReviewControlValves);
      this.generateAFMSTable(costReviewAttributes.costReviewAfms);
      this.generateExhaustFansTable(costReviewAttributes.costReviewExhaustFans);
      this.generateHumidifiersTable(costReviewAttributes.costReviewHumidifiers);
      this.generateAirHandlersTable(costReviewAttributes.costReviewAirHandlers);
    }
    this.generateElectricalWiringTable(costReviewAttributes.costReviewElectricalWiring, costReviewAttributes.costReviewWiringInterlocks);
    this.generateDampersTable(costReviewAttributes.costReviewDampers);
    this.generateSmokeDampersTable(costReviewAttributes.costReviewSmokeDampers);
    if (this.estimateType === EstimateType.EstimateGreater150K) {
      this.generateMeteringTable(costReviewAttributes.costReviewMetering);
      this.generateIntegrationTable(costReviewAttributes.costReviewIntegration);
    }
  }

  //#region COMMON COST REVIEW MAPPING FUNCTIONS

  getRequestDetailsTable(): void {
    let requestDetailsDataRows: IData[] = [];
    const isRoutingCancelledBySystem = this.costReviewStatus === CostReviewStatus.NotStarted && !!this.reviewData?.isAutoUnlockBySystem;
    let routingStatusValue = this.getCostReviewStatusDisplayValue(this.costReviewStatus);
    if (isRoutingCancelledBySystem) {
      routingStatusValue = `${routingStatusValue} ${this.translate.instant('COST_REVIEW_ROUTING_CANCELLED_BY_SYSTEM')}`;
    }
    this.createDisplayRow(this.translate.instant('COST_REVIEW_ROUTING_STATUS'), routingStatusValue, requestDetailsDataRows);

    // When the system cancelled the routing the earlier approvers no longer apply, so only the status row is shown.
    if (!isRoutingCancelledBySystem && this.costReviewerList.length > CONSTANTS.ZERO) {
      this.costReviewerList.forEach(reviewer => {
        const reviewerLabel = `Approver: [${this.getCostReviewerTypeDisplayValue(reviewer.reviewerTypeID)}] ${reviewer.reviewerName ?? ''}`;
        const formattedDate = this.datePipe.transform(reviewer.closeDate, 'dd-MMM-yy');
        let statusDisplayValue = '';
        if ((reviewer.reviewerStatus === CostReviewStatus.Approved || reviewer.reviewerStatus === CostReviewStatus.Denied) && formattedDate !== null && formattedDate !== undefined) {
          statusDisplayValue = `${this.getCostReviewStatusDisplayValue(reviewer.reviewerStatus as CostReviewStatus | null | undefined)} ${this.translate.instant('ON')} ${formattedDate}`;
        } else {
          statusDisplayValue = this.getCostReviewStatusDisplayValue(reviewer.reviewerStatus as CostReviewStatus | null | undefined);
        }
        this.createDisplayRow(reviewerLabel, statusDisplayValue, requestDetailsDataRows);
      });
    }

    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('REQUEST_DETAILS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT, width: this.FIXED_SECOND_COLUMN_WIDTH }
      ],
      data: requestDetailsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  private getCostReviewStatusDisplayValue(status: CostReviewStatus | null | undefined): string {
    switch (status) {
      case CostReviewStatus.Pending:
         return this.translate.instant('COST_REVIEW_STATUS_PENDING');
      case CostReviewStatus.InProcess:
        return this.translate.instant('COST_REVIEW_STATUS_IN_PROGRESS');
      case CostReviewStatus.Approved:
        return this.translate.instant('COST_REVIEW_STATUS_APPROVED');
      case CostReviewStatus.Denied:
         return this.translate.instant('COST_REVIEW_STATUS_DENIED');
      case CostReviewStatus.NotStarted:
      case CostReviewStatus.None:
         return this.translate.instant('COST_REVIEW_STATUS_NOT_STARTED');
      default:
        return this.translate.instant('COST_REVIEW_STATUS_NOT_STARTED');
    }
  }

  private getCostReviewerTypeDisplayValue(reviewerTypeId: number | null): string {
    if (reviewerTypeId === null || reviewerTypeId === undefined) {
      return '';
    }
    const reviewerType = this.costReviewerType.find(type => type.id === reviewerTypeId);
    return reviewerType ? reviewerType.description : reviewerTypeId.toString();
  }

  private createDisplayRow(label: string, value: string, dataRows: IData[]): void {
    dataRows.push({
      data: [
        { data: label, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: value, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ]
    });
  }

  generateSummaryTable(costReviewSummary: CostReviewSummary): void {

    let summaryDataRows: IData[] = [];
    // Common Fields for all Estimate types of Cost Review
    const summaryFieldsMap: ChecklistReportGenericFieldMap<CostReviewSummary>[] = [
      { label: this.translate.instant('COST_REVIEW_PROJECT_NAME'), key: 'projectName' },
      { label: this.translate.instant('COST_REVIEW_ACCOUNT_NAME'), key: 'customerName' },
      { label: this.translate.instant('COST_REVIEW_SHIP_TO_ADDRESS'), key: 'shipToLocation' },
      { label: this.translate.instant('COST_REVIEW_PROJECT_CATEGORY_VERIFICATION'), key: 'projectCategoryVerification' }
    ];
    this.getSummaryFieldsMapped(summaryFieldsMap, costReviewSummary, summaryDataRows);

    switch (this.costReviewType) {
      case CostReviewType.FireEnUs:
        this.generateFireSummary(costReviewSummary, summaryDataRows);
        break;
      case CostReviewType.SecurityEnUs:
        this.generateSecuritySummary(costReviewSummary, summaryDataRows);
        break;
      case CostReviewType.AutomationEnUs:
      case CostReviewType.ElectricalEnUs:
        this.generateAutomationSummary(costReviewSummary, summaryDataRows);
        break;
    }

    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('SUMMARY'), isSubHeader: true, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: summaryDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  private generateFireSummary(costReviewSummary: CostReviewSummary, summaryDataRows: IData[]): void {
    // Fire Order: ProjectName → CustomerName → ShipToLocation → ProjectCategoryVerification → AuthorityHavingJurisdiction → 
    // NationalOrEnterpriseAccount → ConsultingEngineer → [150K fields] → ProjectStartDate → ProjectEndDate → ProjectDuration → 
    // EstimateDepth → IsCollaborative → FireTaskBasedEstimating → SMApprovalRecieved → AlternatesIncluded → OpportunityLinked → 
    // Construction → SufficientSpace → GraphicsStandardVerifiedWithGIS → VerticalMarket → ProjectScope → ClarityOfScope → 
    // CommissioningRequirements → ResourceRequirements → ThirdPartyResources → SiteAccess → HighWorkArea → IsCDP → 
    // IsEnergyOMReviewRequired → ZteApprovalReceived
    let fireBaseMap: ChecklistReportGenericFieldMap<CostReviewSummary>[] = [
      { label: this.translate.instant('COST_REVIEW_AUTHORITY_HAVING_JURISDICTION'), key: 'authorityHavingJurisdiction' },
      { label: this.translate.instant('COST_REVIEW_NATIONAL_OR_ENTERPRISE_ACCOUNT'), key: 'nationalOrEnterpriseAccount' , parameterId: CONSTANTS.YES_NO},
      { label: this.translate.instant('COST_REVIEW_CONSULTING_ENGINEER'), key: 'consultingEngineer' },
      { label: this.translate.instant('COST_REVIEW_ARCHITECTURE_ENGINEERING_FIRM'), key: 'architectureFirm' },
      { label: this.translate.instant('COST_REVIEW_CONSTRUCTION_MANAGER'), key: 'constructionManager' },
      { label: this.translate.instant('COST_REVIEW_JOBSITE_RT_MILEAGE'), key: 'jobsiteRtMileage' }
    ];
    this.getSummaryFieldsMapped(fireBaseMap, costReviewSummary, summaryDataRows);
    this.getProjectStartEndDate(costReviewSummary, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_ESTIMATE_DEPTH'), costReviewSummary.estimateDepth, summaryDataRows, 'ESTIMATE_DEPTH');
    const isCollaborativeEstimate = this.commonDataService.isCollaborationEstimate() ? CONSTANTS.YES : CONSTANTS.NO;
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_COLLABORATIVE'), isCollaborativeEstimate, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_FIRE_TASK_BASED_ESTIMATING'), costReviewSummary.fireTaskBasedEstimating, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_SM_APPROVAL_RECEIVED'), costReviewSummary.smApprovalRecieved, summaryDataRows, CONSTANTS.YES_NO);
    this.getFireAutomationCommonFields(costReviewSummary, summaryDataRows);
    this.getFireAutomationProScope(costReviewSummary, summaryDataRows);
    this.getFireAutomationBaseParts(costReviewSummary, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_ZTE_APPROVAL_RECEIVED'), costReviewSummary.zteApprovalReceived, summaryDataRows, CONSTANTS.YES_NO);
    const taskBasedLaborSettings = this.commonDataService.getTaskBasedLaborSettings();
    if (taskBasedLaborSettings && !this.commonFunctionsService.hasJobDetailsErrors(taskBasedLaborSettings)) {
      this.fillJobDetailsInFireSummary(taskBasedLaborSettings, summaryDataRows);
    }
  }

  private fillJobDetailsInFireSummary(taskBasedLaborSettings: TaskBasedLaborSettings, summaryDataRows: IData[]) {
    this.createRowOneValueData(this.translate.instant('JOB_DETAILS'), CONSTANTS.EMPTY_STRING, summaryDataRows);
    const selectedOfferings = this.commonFunctionsService.getSelectedOfferings(taskBasedLaborSettings);
      if (selectedOfferings.length > CONSTANTS.ZERO) {
        this.createRowOneValueData(this.translate.instant('PRODUCT_OFFERING'), selectedOfferings.map(offering => this.translate.instant(offering)).join(', '), summaryDataRows,CONSTANTS.EMPTY_STRING,1);
      }
      if (taskBasedLaborSettings.selectedProductTypeValue) {
        this.createRowOneValueData(this.translate.instant('PRODUCT_TYPE'), taskBasedLaborSettings.selectedProductTypeValue, summaryDataRows,CONSTANTS.EMPTY_STRING,1);
      }
      if (taskBasedLaborSettings.fireJobDetailsSiteSize) {
        this.createRowOneValueData(this.translate.instant('SITE_SIZE_SQUARE_FOOTAGE'), taskBasedLaborSettings.fireJobDetailsSiteSize, summaryDataRows,CONSTANTS.EMPTY_STRING,1);
      }
      if (taskBasedLaborSettings.isERCESOfferingSelected) {
        this.createRowOneValueData(this.translate.instant('ANTENNA_QTY'), taskBasedLaborSettings.fireJobDetailsAntennaQty, summaryDataRows,CONSTANTS.EMPTY_STRING,1);
      }
      if (taskBasedLaborSettings.isGasSystemOfferingSelected) {
          this.createRowOneValueData(this.translate.instant('AGENT_WEIGHT'), taskBasedLaborSettings.fireJobDetailsAgentWeight, summaryDataRows,CONSTANTS.EMPTY_STRING,1);
          this.createRowOneValueData(this.translate.instant('CLEAN_AGENT_NET_VOL_SPACE'), taskBasedLaborSettings.fireJobDetailsCleanAgentVolSpace, summaryDataRows,CONSTANTS.EMPTY_STRING,1);
      }
  }

  private generateSecuritySummary(costReviewSummary: CostReviewSummary, summaryDataRows: IData[]): void {
    // Security Order: ProjectName → CustomerName → ShipToLocation → ProjectCategoryVerification → [150K fields always] → 
    // ProjectStartDate → ProjectEndDate → ProjectDuration → VerticalMarket → EstimateDepth → IsCollaborative → 
    // OpportunityLinked → ProposalLinked → AlternatesIncluded → ContigencyIncluded → SiemensPrimeBidder
    const securityMap: ChecklistReportGenericFieldMap<CostReviewSummary>[] = [
      { label: this.translate.instant('COST_REVIEW_ARCHITECTURE_FIRM'), key: 'architectureFirm' },
      { label: this.translate.instant('COST_REVIEW_CONSTRUCTION_MANAGER'), key: 'constructionManager' },
      { label: this.translate.instant('COST_REVIEW_JOBSITE_RT_MILEAGE'), key: 'jobsiteRtMileage' }
    ];
    this.getSummaryFieldsMapped(securityMap, costReviewSummary, summaryDataRows);
    this.getProjectStartEndDate(costReviewSummary, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_VERTICAL_MARKET'), costReviewSummary.verticalMarket, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_ESTIMATE_DEPTH'), costReviewSummary.estimateDepth, summaryDataRows, 'ESTIMATE_DEPTH');
    const isCollaborativeEstimate = this.commonDataService.isCollaborationEstimate() ? CONSTANTS.YES : CONSTANTS.NO;
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_COLLABORATIVE'), isCollaborativeEstimate, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_PROPOSAL_LINKED'), costReviewSummary.proposalLinked, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_ALTERNATES_INCLUDED'), costReviewSummary.alternatesIncluded, summaryDataRows, CONSTANTS.YES_NO);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_CONTINGENCY_INCLUDED'), this.decimalPrecisionPipe.transform(costReviewSummary.contigencyIncluded) + '%', summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_SIEMENS_A_PRIME_BIDDER'), costReviewSummary.siemensPrimeBidder, summaryDataRows, CONSTANTS.YES_NO);
  }

  private generateAutomationSummary(costReviewSummary: CostReviewSummary, summaryDataRows: IData[]): void {
    // Automation Order: ProjectName → CustomerName → ShipToLocation → ProjectCategoryVerification → ConsultingEngineer → 
    // [150K fields] → ProjectStartDate → ProjectEndDate → ProjectDuration → EstimateDepth → IsCollaborative → 
    // AlternatesIncluded → OpportunityLinked → Construction → SufficientSpace → GraphicsStandardVerifiedWithGIS → 
    // VerticalMarket → LaborEfficiency → ProjectScope → ClarityOfScope → CommissioningRequirements → MvReporting → 
    // ResourceRequirements → ThirdPartyResources → SiteAccess → HighWorkArea → IsCDP → IsEnergyOMReviewRequired
    const automationFieldsMap: ChecklistReportGenericFieldMap<CostReviewSummary>[] = [
      { label: this.translate.instant('COST_REVIEW_CONSULTING_ENGINEER'), key: 'consultingEngineer' },
      { label: this.translate.instant('COST_REVIEW_ARCHITECTURE_ENGINEERING_FIRM'), key: 'architectureFirm' },
      { label: this.translate.instant('COST_REVIEW_CONSTRUCTION_MANAGER'), key: 'constructionManager' },
      { label: this.translate.instant('COST_REVIEW_JOBSITE_RT_MILEAGE'), key: 'jobsiteRtMileage' }
    ];
    this.getSummaryFieldsMapped(automationFieldsMap, costReviewSummary, summaryDataRows);
    this.getProjectStartEndDate(costReviewSummary, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_ESTIMATE_DEPTH'), costReviewSummary.estimateDepth, summaryDataRows, 'ESTIMATE_DEPTH');
    const isCollaborativeEstimate = this.commonDataService.isCollaborationEstimate() ? CONSTANTS.YES : CONSTANTS.NO;
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_COLLABORATIVE'), isCollaborativeEstimate, summaryDataRows);
    this.getFireAutomationCommonFields(costReviewSummary, summaryDataRows);
    if(this.business === CONSTANTS.BUSINESS_AUTOMATION) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_LABOR_EFFICIENCY'), costReviewSummary.laborEfficiency.toString(), summaryDataRows);
    }
    this.getFireAutomationProScope(costReviewSummary, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_MV_REPORTING'), costReviewSummary.mvReporting, summaryDataRows, CONSTANTS.YES_NO);
    this.getFireAutomationBaseParts(costReviewSummary, summaryDataRows);
  }

  private getFireAutomationBaseParts(costReviewSummary: CostReviewSummary, summaryDataRows: IData[]) {
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_RESOURCE_REQUIREMENTS'), costReviewSummary.resourceRequirements, summaryDataRows, 'RESOURCE_REQUIREMENTS');
    const thirdPartyResourcesValue = costReviewSummary.thirdPartyResources;
    if (thirdPartyResourcesValue !== undefined && thirdPartyResourcesValue !== null) {
      const thirdPartyResourcesMappedValue = this.getParameterValue(CONSTANTS.YES_NO_UNKNOWN, thirdPartyResourcesValue) ?? String(thirdPartyResourcesValue);
      const thirdPartyResourceRequiredDescription = costReviewSummary.thirdPartyResourceRequiredDescription ?? '';

      summaryDataRows.push(
        this.createRowTwoValueData(
          this.translate.instant('COST_REVIEW_THIRD_PARTY_RESOURCES'),
          thirdPartyResourcesMappedValue,
          thirdPartyResourceRequiredDescription
        )
      );
    }
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_SITE_ACCESS'), costReviewSummary.siteAccess, summaryDataRows, CONSTANTS.YES_NO_UNKNOWN);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_HIGH_WORK_AREA'), costReviewSummary.highWorkArea, summaryDataRows, CONSTANTS.YES_NO);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_CONNECTED_DIGITAL_PACKAGE'), costReviewSummary.isCDP, summaryDataRows);
    if(this.isServiceProjectSubsegment()){
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_ENERGY_OM_COST_REVIEW_REQUIRED'), costReviewSummary.isEnergyOMReviewRequired, summaryDataRows);
    }
  }

  private getFireAutomationProScope(costReviewSummary: CostReviewSummary, summaryDataRows: IData[]) {
    const projectScopeValue = costReviewSummary.projectScope;
    if (projectScopeValue) {
      const projectScopeMappedValue = this.getParameterValue('PROJECT_SCOPE', projectScopeValue) ?? String(projectScopeValue);
      const projectScopeDescription = costReviewSummary.projectScopeDescription ?? '';

      summaryDataRows.push(
        this.createRowTwoValueData(
          this.translate.instant('COST_REVIEW_PROJECT_SCOPE'),
          projectScopeMappedValue,
          projectScopeDescription
        )
      );
    }

    let fireAutomationProScopeMap: ChecklistReportGenericFieldMap<CostReviewSummary>[] = [
      { label: this.translate.instant('COST_REVIEW_CLARITY_OF_SCOPE'), key: 'clarityOfScope', parameterId: 'CLARITY_OF_SCOPE' },
      { label: this.translate.instant('COST_REVIEW_COMMISSIONING_REQUIREMENTS'), key: 'commissioningRequirements', parameterId: 'COMMISSIONING_REQUIREMENTS' }
    ];
    this.getSummaryFieldsMapped(fireAutomationProScopeMap, costReviewSummary, summaryDataRows);
  }

  private getFireAutomationCommonFields(costReviewSummary: CostReviewSummary, summaryDataRows: IData[]) {
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_ALTERNATES_INCLUDED'), costReviewSummary.alternatesIncluded, summaryDataRows, CONSTANTS.YES_NO);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_OPPORTUNITY_LINKED'), costReviewSummary.opportunityLinked, summaryDataRows);
    let fireAutomationCommonMap: ChecklistReportGenericFieldMap<CostReviewSummary>[] = [
      { label: this.translate.instant('COST_REVIEW_CONSTRUCTION'), key: 'construction', parameterId: 'CONSTRUCTION' },
      { label: this.translate.instant('COST_REVIEW_SUFFICIENT_SPACE'), key: 'sufficientSpace', parameterId: CONSTANTS.YES_NO }
    ];

    if (costReviewSummary.isGraphicsStandardVerifiedWithGISVisible) {
      fireAutomationCommonMap.push({
        label: this.translate.instant('COST_REVIEW_GRAPHICS_STANDARD_VERIFIED_WITH_GIS'),
        key: 'graphicsStandardVerifiedWithGIS',
        parameterId: CONSTANTS.YES_NO
      });
    }

    this.getSummaryFieldsMapped(fireAutomationCommonMap, costReviewSummary, summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_VERTICAL_MARKET'), costReviewSummary.verticalMarket, summaryDataRows);
  }

  getProjectStartEndDate(costReviewSummary: CostReviewSummary, summaryDataRows: IData[]) {
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_PROJECT_START_DATE'), this.datePipe.transform(costReviewSummary.projectStartDate, CONSTANTS.DATE_FORMAT), summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_PROJECT_END_DATE'), this.datePipe.transform(costReviewSummary.projectEndDate, CONSTANTS.DATE_FORMAT), summaryDataRows);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_DURATION_DAYS'), costReviewSummary.projectDuration.toString(), summaryDataRows);
  }

  getSummaryFieldsMapped(summaryFieldsMap: ChecklistReportGenericFieldMap<CostReviewSummary>[], costReviewSummary: CostReviewSummary, summaryDataRows: IData[]) {
    summaryFieldsMap.forEach(field => {
      const value = costReviewSummary[field.key];
      if (field.key === 'projectCategoryVerification') {
        const projectCategoryValue = this.getProjectCategoryVerificationValue(value as number);
        this.createRowOneValueData(field.label, projectCategoryValue, summaryDataRows);
        return;
      }
      if (field.key === 'jobsiteRtMileage') {
        if (this.business === CONSTANTS.BUSINESS_SECURITY || this.estimateType === EstimateType.EstimateGreater150K) {
          this.createRowOneValueData(field.label, value, summaryDataRows);
        }
        return;
      }
      if (field.key === 'sufficientSpace') {
        if (costReviewSummary.construction === 1) {
          const getSufficientSpace = costReviewSummary.sufficientSpace === 1 ? CONSTANTS.YES : CONSTANTS.NO;
          this.createRowOneValueData(field.label, getSufficientSpace, summaryDataRows);
        }
        return;
      }
      if (value !== undefined && value !== null && value !== '') {
        const displayValue = field.parameterId ? this.getParameterValue(field.parameterId, value) : value;
        this.createRowOneValueData(field.label, displayValue, summaryDataRows);
      }
    });
  }

  getParameterValue(parameterId: string, value: allTypes): string | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    return this.modifiedReviewParameterData.get(`${parameterId}_${value}`);
  }

  getParameterValuesForArray(parameterId: string, value: allTypes): string | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    return this.modifiedReviewParameterData.get(`${parameterId}_${value}`);
  }

  getProjectCategoryVerificationValue(value: number): string {
    switch (value) {
      case ProjectCategoryVerification.ProjectCategory0:
        return CONSTANTS.PROJECT_CATEGORY_W;

      case ProjectCategoryVerification.ProjectCategory1:
        return CONSTANTS.PROJECT_CATEGORY_T;

      case ProjectCategoryVerification.ProjectCategory2:
        return CONSTANTS.PROJECT_CATEGORY_S;

      default:
        return CONSTANTS.EMPTY_STRING;
    }
  }


   private getCultureIdFromPreferences(): number {
    const locale = this.commonDataService.getUserLanguagePreference();

    if (locale === CONSTANTS.LANGUAGE_FR_CA) {
      return CULTURE_ID.FR_CA;
    }

    if (locale === CONSTANTS.LANGUAGE_EN_CA) {
      return CULTURE_ID.EN_CA;
    }

    return CULTURE_ID.EN_US;
  }

  private getEstimateSubsegmentId(): number | undefined {
    const subsegmentId = this.commonDataService.getEstimateData()?.subSegmentID ?? null;
    return subsegmentId === null ? undefined : subsegmentId;
  }

  private getServiceProjectSubsegmentIdsByCulture(cultureId: number): readonly number[] {
    if (cultureId === CULTURE_ID.EN_US) {
      return [
        CONSTANTS.AUTOMATION_SERVICE_PROJECT_US,
        CONSTANTS.MECHANICAL_SERVICE_PROJECT_US,
      ];
    }

    return [
      CONSTANTS.AUTOMATION_SERVICE_PROJECT_CN_EN,
      CONSTANTS.AUTOMATION_SERVICE_PROJECT_CN_FR,
      CONSTANTS.MECHANICAL_SERVICE_PROJECT_CN_EN,
      CONSTANTS.MECHANICAL_SERVICE_PROJECT_CN_FR,
    ];
  }

  isServiceProjectSubsegment(): boolean {
    const resolvedSubsegmentId = this.getEstimateSubsegmentId();
    if (!resolvedSubsegmentId) {
      return false;
    }

    const cultureId = this.getCultureIdFromPreferences();
    return this.getServiceProjectSubsegmentIdsByCulture(cultureId).includes(resolvedSubsegmentId);
  }

  generateEngExceptionTableRows(costReviewAutomation: CostReviewAttributes): IData[] {
    const engineerExceptionsDataRows: IData[] = [];
      this.createRowOneValueData(
          this.translate.instant('REVIEW_SOL_LABOR_AND_COSTS_TAB.BRANCH_ZOM_APPROVAL_RECEIVED'),
          costReviewAutomation.engineerApprovalReceived,
          engineerExceptionsDataRows,
          CONSTANTS.YES_NO
        );

      if (costReviewAutomation.engineerApproverEmail){
        this.createRowOneValueData(
          this.translate.instant('ENGINEER_APPROVER_EMAIL'),
          costReviewAutomation.engineerApproverEmail,
          engineerExceptionsDataRows
        );
      }
      if (costReviewAutomation.engineerReasonForException){
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_REASON_FOR_EXCEPTION'),
          costReviewAutomation.engineerReasonForException,
          engineerExceptionsDataRows
        );
      }
    return engineerExceptionsDataRows;
  }

  generatePlannedCostTable(costReviewAttributes : CostReviewAttributes): void {
    let plannedCostDataRows: IData[] = [];
    let plannedCostDataFooter: IFooter[] = [];
    let plannedCostLaborFields: IData[] = [];
    let plannedCostLaborFooter: IFooter[] = [];
    let totalItemCost = 0;
    let totalItemTdcPercentage = 0;
    let totalLaborCost = 0;
    let totalLaborTdcPercentage = 0;

    costReviewAttributes?.costReviewPlannedCosts?.forEach(plannedCost => {
      let fieldName = plannedCost.itemName;
      if (plannedCost.targetVisible) {
        const targetValue = plannedCost.itemName.toUpperCase() === 'RISK'
          ? this.commonFunctionsService.convertToPercentage(plannedCost.riskDisplayText)
          : this.commonFunctionsService.convertToPercentage(plannedCost.target);
        fieldName = `${plannedCost.itemName} [${this.translate.instant('COST_REVIEW_MINIMUM')} ${plannedCost.itemName}: ${targetValue}]`;
      }

      const fieldValueOne = this.commonFunctionsService.convertToPercentage(plannedCost.tdcPercent);

      if (this.checkForLaborField(plannedCost)) {
        totalLaborCost += plannedCost.itemCost;
        totalLaborTdcPercentage += plannedCost.tdcPercent;
        // Labor fields: fieldName, cost, TDC%, hours
        plannedCostLaborFields.push(this.createPlannedCostRow(
          fieldName,
          plannedCost.itemCost,
          fieldValueOne,
          plannedCost.hours.toString()
        ));
      } else {
        totalItemCost += plannedCost.itemCost;
        totalItemTdcPercentage += plannedCost.tdcPercent;
        // Item fields: fieldName, cost, TDC%
        plannedCostDataRows.push(this.createPlannedCostRow(
          fieldName,
          plannedCost.itemCost,
          fieldValueOne
        ));
      }
    });

    // Add total rows with totalCost and totalPercentTdc to footer
    plannedCostDataFooter.push(this.createFooterRow(
      this.translate.instant('TOTAL_COLUMN'),
      totalItemCost,
      this.commonFunctionsService.convertToPercentage(totalItemTdcPercentage),
      true
    ));

    plannedCostLaborFooter.push(this.createFooterRow(
      this.translate.instant('TOTAL_COLUMN'),
      totalLaborCost,
      this.commonFunctionsService.convertToPercentage(totalLaborTdcPercentage),
      true
    ));

    this.checklistTableData.push({
      tableHeader: this.reportsCommonService.generateReportTableHeader('PLANNED_COSTS'),
      title: [
        { data: this.translate.instant('COST_REVIEW_ITEM'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: this.translate.instant('COST_REVIEW_COST'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: this.translate.instant('COST_REVIEW_TDC'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT }
      ],
      data: plannedCostDataRows,
      tableFooter: plannedCostDataFooter,
      isPageBreakAfter: true,
      isTableBordered: true
    } ,{
      title: [
        { data: this.translate.instant('COST_REVIEW_LABOR'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: this.translate.instant('COST_REVIEW_COST'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: this.translate.instant('COST_REVIEW_TDC'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: this.translate.instant('COST_REVIEW_HOURS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT }
      ],
      data: plannedCostLaborFields,
      tableFooter: plannedCostLaborFooter,
      isPageBreakAfter: false,
      isTableBordered: true
    } );
  if (costReviewAttributes?.isEngineerExceptionApplicable) {
    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('REVIEW_SOL_LABOR_AND_COSTS_TAB.ENG_LABOR_BELOW_EXPECTED_TDC'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT, width: this.FIXED_SECOND_COLUMN_WIDTH },
      ],
      data: this.generateEngExceptionTableRows(costReviewAttributes as CostReviewAttributes),
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  }

  private checkForLaborField(item: CostReviewPlannedCosts): boolean {
    return item.positionId === PlannedCostLaborFields.PROJECT_MANAGER ||
      item.positionId === PlannedCostLaborFields.MECHANICAL ||
      item.positionId === PlannedCostLaborFields.ELECTRICAL ||
      item.positionId === PlannedCostLaborFields.SPECIALIST ||
      item.positionId === PlannedCostLaborFields.ENGINEER ||
      item.positionId === PlannedCostLaborFields.NENGINEER ||
      item.positionId === PlannedCostLaborFields.QENGINEER ||
      item.positionId === PlannedCostLaborFields.CENGINEER ||
      item.positionId === PlannedCostLaborFields.EENGINEER ||
      item.positionId === PlannedCostLaborFields.LENGINEER ||
      item.positionId === PlannedCostLaborFields.GRAPHICS ||
      item.positionId === PlannedCostLaborFields.PROGRAMMING ||
      item.positionId === PlannedCostLaborFields.DIGITAL_DEPLOYMENT_SPECIALIST;
  }

  private formatMarginValue(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return CONSTANTS.EMPTY_STRING;
    }
    const precision =  this.commonDataService.getDecimalPrecision();
    return `${this.decimalPrecisionPipe.transform(roundToPrecision(value, precision))}%`;
  }

  generateMarginExceptionsTable(marginExceptions: CostReviewMarginExceptions, costReviewSummary: CostReviewSummary): void {
    let marginExceptionsDataRows: IData[] = [];
    let marginExceptionsMap: { label: string; key: string, parameterId?: string }[] = [
      { label: this.translate.instant('COST_REVIEW_PROJECT_START_DATE'), key: 'projectStartDate', parameterId: '' },
      { label: this.translate.instant('COST_REVIEW_PROJECT_END_DATE'), key: 'projectEndDate', parameterId: '' }
    ];
     if(!marginExceptions) {
      return;
    }
    if(marginExceptions.isEstimateClassificationApplicable){
       this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SYSTEM_TYPE'),
        marginExceptions.estimateClassification,
        marginExceptionsDataRows,
        CONSTANTS.SIEMENS_THIRD_PARTY
      );
  }

    const estimatedMargin = this.formatMarginValue(marginExceptions.estimatedMargin);
    const estimatedMarginMinimum = this.formatMarginValue(marginExceptions.estimatedMarginMinimum);
    const estimatedMarginVariance = this.formatMarginValue(marginExceptions.estimatedMarginVariance);

    marginExceptionsDataRows.push({
      data: [
        { data: this.translate.instant('COST_REVIEW_ESTIMATED_MARGIN'), columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
        {
          data: `${estimatedMargin} ${CONSTANTS.BLANK_SPACE} [Minimum: ${estimatedMarginMinimum}]`,
          columnspan: 1,
          hierarchyLevel: 0,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: `Variance: ${estimatedMarginVariance}`,
          columnspan: 1,
          hierarchyLevel: 0,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ]
    });

    if (marginExceptions?.isReasonForExceptionApplicable) {
      this.createRowOneValueData(
        this.translate.instant('REVIEW_SOL_MARGINS_TAB.SM_OR_BGM_APPROVAL_RECEIVED'),
        marginExceptions.smOrBgmApprovalRecieved,
        marginExceptionsDataRows,
        CONSTANTS.YES_NO
      );
    }

    if (marginExceptions.approverEmailId) {
      this.createRowOneValueData(
        this.translate.instant('REVIEW_SOL_MARGINS_TAB.APPROVERS_EMAIL'),
        marginExceptions.approverEmailId,
        marginExceptionsDataRows
      );
    }

    if (marginExceptions.reasonForException) {
      this.createRowOneValueData(
        this.translate.instant('REVIEW_SOL_MARGINS_TAB.REASON_FOR_EXCEPTION'),
        marginExceptions.reasonForException,
        marginExceptionsDataRows
      );
    }
    marginExceptionsMap.forEach(item => {
      const { label, key } = item;
      let value = marginExceptions[key as keyof CostReviewMarginExceptions];
      if (key === 'projectStartDate') {
        value = this.datePipe.transform(costReviewSummary?.projectStartDate, 'MM/dd/yyyy');
      } else if (key === 'projectEndDate') {
        value = this.datePipe.transform(costReviewSummary?.projectEndDate, 'MM/dd/yyyy');
      }
      const displayValue = item.parameterId
        ? this.modifiedReviewParameterData.get(`${item.parameterId}_${value}`)
        : value;
      this.createRowOneValueData(label, displayValue, marginExceptionsDataRows);
    });
    if (marginExceptions.escalationApplied !== null && marginExceptions.escalationApplied !== undefined) {
      const escalationCost = this.multilingualCurrencyPipe.transform(marginExceptions.escalationCost ?? 0);
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_ESCALATION_APPLIED'),
        `${marginExceptions.escalationApplied} ${CONSTANTS.BLANK_SPACE} [${escalationCost}]`,
        marginExceptionsDataRows
      );
    }

    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('COST_REVIEW_MARGIN_EXPECTATIONS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: marginExceptionsDataRows,
      tableFooter: [],
      isPageBreakAfter: true,
      isTableBordered: true
    });
  }

  generateGeneralConditionsTable(generalConditions: CostReviewGeneralConditions, isCpmVisible: boolean): void {
    let generalConditionsDataRows: IData[] = [];
    if (generalConditions?.isGeneralConditionsApplicable ||
       (this.estimateType === EstimateType.EstimateGreater150K && this.costReviewType === CostReviewType.AutomationEnUs)) {
      const isEstimateGreaterThan150K = this.estimateType === EstimateType.EstimateGreater150K;
      const isAutomationOrMechanicalBusiness = this.isAutomationOrMechanicalBusiness();
      const isFireBusiness = this.business === CONSTANTS.BUSINESS_FIRE;

      if (isFireBusiness || isEstimateGreaterThan150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_MINORITY'), generalConditions.minority, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_PREVAILING_WAGE'), generalConditions.prevailingWage, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_BUY_AMERICA'), generalConditions.buyAmerica, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_BUY_AMERICAN_ACT'), generalConditions.buyAmericanAct, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_OVERTIME_ESTIMATED'), generalConditions.overtimeEstimated, generalConditionsDataRows);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_ESCALATION_INCLUDED'), generalConditions.escalationIncluded, generalConditionsDataRows);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_LIFT_COSTS'), generalConditions.liftCosts, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.addPercentageRow(this.translate.instant('COST_REVIEW_SPARE_PARTS_PERCENT'), generalConditions.sparePartsPercent, generalConditionsDataRows);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_DEVICE_ACCESS_PANELS'), generalConditions.deviceAccessPanels, generalConditionsDataRows, CONSTANTS.YES_NO);
      }

      if (isEstimateGreaterThan150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_TRADE_AGREEMENT_ACT'), generalConditions.tradeAgreementAct, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_BIM_MODELING'), generalConditions.bimModeling, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_SCHEDULER_ESTIMATE'), generalConditions.schedulerEstimate, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.addCurrencyRow(this.translate.instant('COST_REVIEW_TRAVEL_EXPENSES_PLANNED'), generalConditions.travelExpensesPlanned, generalConditionsDataRows);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_TRAILER_RENTAL_SPACE'), generalConditions.trailerRentalSpace, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_OFFICE_SUPPLIES_COSTS'), generalConditions.officeSuppliesCosts, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_BIM360_FIELD_CONFIGURATION'), generalConditions.bim360FieldConfiguration, generalConditionsDataRows, CONSTANTS.YES_NO);
        this.mapGenConditionsEst150K(generalConditions, generalConditionsDataRows);
      }

      if (isAutomationOrMechanicalBusiness && isEstimateGreaterThan150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_UTILITIES_COSTS'), generalConditions.utilitiesCosts, generalConditionsDataRows, CONSTANTS.YES_NO);
      }

      if (generalConditions.projectIsPhased !== undefined && generalConditions.projectIsPhased !== null) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_PROJECT_IS_PHASED'), generalConditions.projectIsPhased, generalConditionsDataRows, CONSTANTS.YES_NO);
        if (generalConditions.projectIsPhased === CONSTANTS.ONE) {
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_IF_YES_DESCRIBE'), generalConditions.ifYesDescribe, generalConditionsDataRows);
        }
      }

      if (isFireBusiness && isEstimateGreaterThan150K) {
        this.addCurrencyRow(this.translate.instant('COST_REVIEW_PARKING_FEES'), generalConditions.parkingFees, generalConditionsDataRows);
      }

      if (isAutomationOrMechanicalBusiness) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_EQUIPMENT_ENCLOSURES'), generalConditions.equipmentEnclosures, generalConditionsDataRows, CONSTANTS.YES_NO);
      }

      if (isFireBusiness) {
        this.addPercentageRow(this.translate.instant('COST_REVIEW_SPARE_POINT_CAPACITY'), generalConditions.sparePointCapacity, generalConditionsDataRows);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_TAX_REQUIRED'), generalConditions.taxRequired, generalConditionsDataRows, 'SALES_USE_NONE');
      }

      if (isAutomationOrMechanicalBusiness && isEstimateGreaterThan150K && generalConditions.salesTax) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_SALES_TAX'), generalConditions.salesTax, generalConditionsDataRows, CONSTANTS.YES_NO);
      }

      if (isCpmVisible) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_CPM_HOURS_ESTIMATED'), generalConditions.cpmHoursEstimated, generalConditionsDataRows, CONSTANTS.YES_NO);
      }
    

    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('COST_REVIEW_GENERAL_CONDITIONS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT, width: this.FIXED_SECOND_COLUMN_WIDTH },
      ],
      data: generalConditionsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  }

  private isAutomationOrMechanicalBusiness(): boolean {
    return this.business === CONSTANTS.BUSINESS_AUTOMATION || this.business === CONSTANTS.BUSINESS_MECHANICAL;
  }

  private addCurrencyRow(label: string, value: number | null | undefined, dataRows: IData[]): void {
    if (value !== undefined && value !== null) {
      dataRows.push(this.createRowWithValuePrefix(label, value));
    }
  }

  private addPercentageRow(label: string, value: number | null | undefined, dataRows: IData[]): void {
    if (value !== undefined && value !== null) {
      this.createRowOneValueData(label, this.commonFunctionsService.convertToPercentage(value), dataRows);
    }
  }

  private mapGenConditionsEst150K(generalConditions: CostReviewGeneralConditions, generalConditionsDataRows: IData[]) {
    if (this.estimateType === EstimateType.EstimateGreater150K) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_FIELD_PROCUREMENT_MANAGER'), generalConditions.fieldProcurementManager, generalConditionsDataRows, CONSTANTS.YES_NO);
      if (generalConditions.temporarySitePersonnel && generalConditions.temporarySitePersonnel.length > 0) {
        const sortedPersonnel = [...generalConditions.temporarySitePersonnel].sort((a, b) => a - b);
        sortedPersonnel.forEach(personnelValue => {
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_TEMPORARY_SITE_PERSONNEL'), personnelValue, generalConditionsDataRows, 'TEMPORARY_SITE_PERSONNEL');
        });
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SERVICE_CONTRACT'), generalConditions.serviceContract, generalConditionsDataRows, CONSTANTS.YES_NO);
    }
  }

  generateBidDocumentationAvailableTable(bidDocumentationAvailable: CostReviewBidDocumentationAvailable): void {
    let bidDocumentationDataRows: IData[] = [];
    if (bidDocumentationAvailable?.isBidDocumentationApplicable) {
      const isSecurityBusiness = this.costReviewType === CostReviewType.SecurityEnUs;
      const isFireBusiness = this.costReviewType === CostReviewType.FireEnUs;
      const isEstimateGreaterThan150K = this.estimateType === EstimateType.EstimateGreater150K;

      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.JOB_SPECIFICATIONS'), bidDocumentationAvailable.jobSpecifications, bidDocumentationDataRows, CONSTANTS.YES_NO);
      if (isSecurityBusiness) {
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.SECURITY_SYSTEM_ELECTRICAL_DRAWINGS'), bidDocumentationAvailable.securitySystemElectricalDrawings, bidDocumentationDataRows, CONSTANTS.YES_NO);
      } else {
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.MECHANICAL_DRAWINGS'), bidDocumentationAvailable.mechanicalDrawings, bidDocumentationDataRows, CONSTANTS.YES_NO);
      }
      if (isFireBusiness) {
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.FIRE_SYSTEM_ELECTRICAL_DRAWINGS'), bidDocumentationAvailable.fireSystemElectricalDrawings, bidDocumentationDataRows, CONSTANTS.YES_NO);
      }
      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.EXCEPTIONS_CLARIFICATIONS'), bidDocumentationAvailable.exceptionsClarifications, bidDocumentationDataRows, CONSTANTS.YES_NO);
      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.SCOPE_OF_WORK_LETTER'), bidDocumentationAvailable.scopeOfWorkLetter, bidDocumentationDataRows, CONSTANTS.YES_NO);
      
      if(bidDocumentationAvailable.projectContractAddenda) {
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.PROJECT_CONTRACT_ADDENDA'), bidDocumentationAvailable.projectContractAddenda, bidDocumentationDataRows);
      }

      if (!isSecurityBusiness) {
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.SMOKE_CONTROL_SEQUENCE'), bidDocumentationAvailable.smokeControlSequence, bidDocumentationDataRows, CONSTANTS.YES_NO);
      }

      if (!isSecurityBusiness && (isEstimateGreaterThan150K || isFireBusiness)) {
        if(bidDocumentationAvailable.producPromotionReceived) {
          this.createRowOneValueData(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.PRODUCT_PROMOTION_RECEIVED'), bidDocumentationAvailable.producPromotionReceived, bidDocumentationDataRows);
        }
        
        this.fillSubsidyAndNotesRow(bidDocumentationAvailable, bidDocumentationDataRows);
      }
      if (isSecurityBusiness) {
        this.createRowOneValueData(
          this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.PRODUCT_DISCOUNT_APPLIED'),
          bidDocumentationAvailable.productDiscount,
          bidDocumentationDataRows,
          CONSTANTS.YES_NO
        );  

        this.fillSubsidyAndNotesRow(bidDocumentationAvailable, bidDocumentationDataRows);

        if(bidDocumentationAvailable.listAllAddenda) {
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_LIST_ALL_ADDENDA'), bidDocumentationAvailable.listAllAddenda, bidDocumentationDataRows);
        } 
      }
    } else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), '', bidDocumentationDataRows);
    }

    // Note: if the text is too long, it will not automatically wrap in the PDF generation need to add max width or handle long text separately
    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('COST_REVIEW_BID_DOCUMENTATION_AVAILABLE'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT, width: this.FIXED_SECOND_COLUMN_WIDTH },
      ],
      data: bidDocumentationDataRows,
      tableFooter: [],
      isPageBreakAfter: true,
      isTableBordered: true
    });
  }

  private fillSubsidyAndNotesRow (bidDocumentationAvailable: CostReviewBidDocumentationAvailable, bidDocumentationDataRows: IData[]): void {
    // FillRowForSubsidyRecieved
    if (bidDocumentationAvailable.subsidyReceived !== undefined && bidDocumentationAvailable.subsidyReceived !== null) {
      bidDocumentationDataRows.push(
        this.createRowWithValuePrefix(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.SUBSIDY_RECEIVED'), bidDocumentationAvailable.subsidyReceived)
      );
    }
      // FillRowForNotesOther
    if(bidDocumentationAvailable.notes) {
      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_BID_DOCUMENTATION_TAB.NOTES'), bidDocumentationAvailable.notes, bidDocumentationDataRows);
    }
  }
  generateContractualDocumentationTable(contractualDocumentation: CostReviewContractualDocumentation): void {
    let contractualDocumentationDataRows: IData[] = [];
    if (contractualDocumentation?.isContractualDocumentationApplicable) {
      const isEstimateGreaterThan150K = this.estimateType === EstimateType.EstimateGreater150K;
      const isAutomationBusiness = this.business === CONSTANTS.BUSINESS_AUTOMATION;

      if (isEstimateGreaterThan150K && contractualDocumentation.originalCompletionDate) {
        const formattedDate = this.datePipe.transform(contractualDocumentation.originalCompletionDate, CONSTANTS.DATE_FORMAT);
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.ORIGINAL_COMPLETION_DATE'), formattedDate || '', contractualDocumentationDataRows);
      }
      if (contractualDocumentation.siemensTermsConditionsUsed !== undefined && contractualDocumentation.siemensTermsConditionsUsed !== null) {
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.SIEMENS_TERMS_AND_CONDITIONS_USED'), contractualDocumentation.siemensTermsConditionsUsed, contractualDocumentationDataRows, CONSTANTS.YES_NO);
      }
      // If NOT using Siemens Terms & Conditions (value != 1), show additional fields
      if (contractualDocumentation?.siemensTermsConditionsUsed === 2) {
        this.getContractualDocsSiemensTermsNotUsed(contractualDocumentation, contractualDocumentationDataRows);
      }
      if (isAutomationBusiness && contractualDocumentation.possibleChangeOrdersIdentified) {
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.POSSIBLE_CHANGE_ORDERS_IDENTIFIED'), contractualDocumentation.possibleChangeOrdersIdentified, contractualDocumentationDataRows);
      }
      if (contractualDocumentation.performanceBondAmount !== undefined && contractualDocumentation.performanceBondAmount !== null) {
        contractualDocumentationDataRows.push(this.createRowWithValuePrefix(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.PERFORMANCE_BOND_AMOUNT'), contractualDocumentation.performanceBondAmount));
      }

      if (contractualDocumentation.paymentBondAmount !== undefined && contractualDocumentation.paymentBondAmount !== null) {
        contractualDocumentationDataRows.push(this.createRowWithValuePrefix(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.PAYMENT_BOND_AMOUNT'), contractualDocumentation.paymentBondAmount));
      }

      if (contractualDocumentation.penetrationRequirements && contractualDocumentation.penetrationRequirements.length > 0) {
        const penetrationValues = contractualDocumentation.penetrationRequirements.map(pr => {
          const val = this.getParameterValue('PENETRATION_REQUIREMENTS', pr);
          return val || pr.toString();
        }).join(', ');
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.PENETRATION_REQUIREMENTS'), penetrationValues, contractualDocumentationDataRows);
      }
      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.TRAINING_SPECIFIED'), contractualDocumentation.trainingSpecified, contractualDocumentationDataRows, CONSTANTS.YES_NO);
      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.CHANGE_ORDER_MARKUP_RESTRICTIONS'), contractualDocumentation.changeOrderMarkupRestrictions, contractualDocumentationDataRows, CONSTANTS.YES_NO);

      if(contractualDocumentation.changeOrderOpportunities) {
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.CHANGE_ORDER_OPPORTUNITIES'), contractualDocumentation.changeOrderOpportunities, contractualDocumentationDataRows);
      }
      // Fields for Estimates > 150K
      if (isEstimateGreaterThan150K) {
        this.getContractualMappingGreaterEstimate(contractualDocumentation, contractualDocumentationDataRows);
      }
    } else {
      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.SECTION_NOT_APPLICABLE'), '', contractualDocumentationDataRows);
    }

    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.TITLE'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT, width: this.FIXED_SECOND_COLUMN_WIDTH },
      ],
      data: contractualDocumentationDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  private getContractualMappingGreaterEstimate(contractualDocumentation: CostReviewContractualDocumentation, contractualDocumentationDataRows: IData[]) {
    const isSecurityBusiness = this.costReviewType === CostReviewType.SecurityEnUs;

    if (!isSecurityBusiness) {
      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.SPECIAL_INSURANCE_CERTIFICATION'), contractualDocumentation.specialInsuranceCertification, contractualDocumentationDataRows, CONSTANTS.YES_NO);
      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.LIABILITY_INSURANCE_SPECIAL_COVERAGE'), contractualDocumentation.liabilityInsurance, contractualDocumentationDataRows, CONSTANTS.YES_NO);
    }

    this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.DEDUCTIONS_OFFERED'), contractualDocumentation.deductionsOffered, contractualDocumentationDataRows, CONSTANTS.YES_NO);

    const testingFromDate = this.datePipe.transform(contractualDocumentation?.testingFromDate, CONSTANTS.DATE_FORMAT) || '';
    const testingToDate = this.datePipe.transform(contractualDocumentation?.testingToDate, CONSTANTS.DATE_FORMAT) || '';
    const testingPeriodValue = testingFromDate && testingToDate
      ? `From: ${testingFromDate} To: ${testingToDate}`
      : CONSTANTS.DEFAULT_NOT_APPLICABLE_STRING;
    this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.TESTING_PERIOD_FAT'), testingPeriodValue, contractualDocumentationDataRows);

    this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.LICENSES_PERMITS'), contractualDocumentation.licensesPermits, contractualDocumentationDataRows, CONSTANTS.YES_NO);
    if(contractualDocumentation.specialSafetyRequirements) {
      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.SPECIAL_SAFETY_REQUIREMENTS'), contractualDocumentation.specialSafetyRequirements, contractualDocumentationDataRows);
    }
  }

  private getContractualDocsSiemensTermsNotUsed(contractualDocumentation: CostReviewContractualDocumentation, contractualDocumentationDataRows: IData[]) {
      this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CONTRACTUAL_DOCUMENTATION_TAB.WARRANTY_PERIOD_MONTHS'), contractualDocumentation.warrantyPeriod.toString(), contractualDocumentationDataRows);

      const contractualFieldsMap: ChecklistReportGenericFieldMap<CostReviewContractualDocumentation>[] = [
        { label: this.translate.instant('COST_REVIEW_LIQUIDATED_DAMAGES_LIABILITY'), key: 'liquidatedDamagesLiability', parameterId: 'LIQUIDATED_DAMAGES_LIABILITY' },
        { label: this.translate.instant('COST_REVIEW_CONSEQUENTIAL_DAMAGES_IN_CONTRACT'), key: 'consequentialDamagesInContract', parameterId: 'CONSEQUENTIAL_DAMAGES_IN_CONTRACT' },
        { label: this.translate.instant('COST_REVIEW_WAIVER_REASON'), key: 'waiverReason', parameterId: 'WAIVER_REASON' },
        { label: this.translate.instant('COST_REVIEW_FIRE_STOPPING'), key: 'fireStopping', parameterId: 'SIEMENS_OTHER_SUBCONTRACTOR' },
        { label: this.translate.instant('COST_REVIEW_PATCHING_PAINTING'), key: 'patchingPainting', parameterId: 'SIEMENS_OTHER' },
        { label: this.translate.instant('COST_REVIEW_DEMOLITION_REMOVAL'), key: 'demolitionRemoval', parameterId: 'SIEMENS_OTHER' }
      ];

      contractualFieldsMap.forEach(field => {
        const rawValue = contractualDocumentation[field.key];
        if (rawValue === undefined || rawValue === null) {
          return;
        }

        const mappedValue = this.getParameterValue(field.parameterId, rawValue);

        // Equivalent to C# !string.IsNullOrEmpty(mappedValue)
        if (mappedValue) {
          this.createRowOneValueData(field.label, mappedValue, contractualDocumentationDataRows);
        }
      });

      if (this.costReviewType !== CostReviewType.SecurityEnUs) {
        if (contractualDocumentation.cleanUp !== undefined && contractualDocumentation.cleanUp !== null) {
          const cleanUpValue = this.getParameterValue('SIEMENS_OTHER', contractualDocumentation.cleanUp);
          this.createRowOneValueData('Clean Up', cleanUpValue, contractualDocumentationDataRows);
        }
      }
  }

  generateProjectMgmtRequirementsTable(projectMgmtRequirements: CostReviewProjectMgmtRequirements): void {
    let projectManagementDataRows: IData[] = [];

    if (projectMgmtRequirements?.isProjectMgmtRequirementsApplicable) {
      if (projectMgmtRequirements.frequencyOfProjectMeetings) {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_FREQUENCY_OF_PROJECT_MEETINGS'),
          projectMgmtRequirements.frequencyOfProjectMeetings,
          projectManagementDataRows
        );
      }

      if(projectMgmtRequirements.documentationRequirements){
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_DOCUMENTATION_REQUIREMENTS'),
          projectMgmtRequirements.documentationRequirements,
          projectManagementDataRows
        );
      }  
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_TIME_ESTIMATED_TO_MANAGE_SCHEDULE'),
        projectMgmtRequirements.timeEstimatedToManageSchedule,
        projectManagementDataRows,
        CONSTANTS.YES_NO
      );

      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_ESTIMATED_PM_HOURS_ON_JOB_SITE'),
        projectMgmtRequirements.estimatedPmHoursOnJobSite.toString(),
        projectManagementDataRows
      );    
      this.checklistTableData.push({
        title: [
          {
            data: this.translate.instant('COST_REVIEW_PROJECT_MANAGEMENT_REQUIREMENTS'),
            columnspan: CONSTANTS.ONE,
            hierarchyLevel: CONSTANTS.ZERO,
            DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
            alignment: CONSTANTS.LEFT_ALIGNMENT
          },
          {
            data: '',
            columnspan: CONSTANTS.ONE,
            hierarchyLevel: CONSTANTS.ZERO,
            DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
            alignment: CONSTANTS.LEFT_ALIGNMENT,
            width: this.FIXED_SECOND_COLUMN_WIDTH
          },
        ],
        data: projectManagementDataRows,
        tableFooter: [],
        isPageBreakAfter: false,
        isTableBordered: true
      });
    }
    else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), '', projectManagementDataRows);
    }
  }

  //#endregion END OF COMMON COST REVIEW EN US MAPPINGS

  //#region COST REVIEW FIRE MAPPINGS

  generateSystemAttributesTable(systemAttributes: CostReviewFireEnUsSystemAttributes): void {
    let systemAttributesDataRows: IData[] = [];

    if (systemAttributes?.isSystemAttributeApplicable) {
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_INSTALL_TYPE'),
        systemAttributes.installType,
        systemAttributesDataRows,
        CONSTANTS.INSTALL_TYPE
      );
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SYSTEM_TYPE'),
        systemAttributes.systemType,
        systemAttributesDataRows,
        CONSTANTS.SYSTEM_TYPE
      );

      if (systemAttributes.additionalSystems && systemAttributes.additionalSystems.length > 0) {
        const sortedAdditionalSystems = [...systemAttributes.additionalSystems].sort((a, b) => a - b);
        sortedAdditionalSystems.forEach(item => {
          this.createRowOneValueData(
            this.translate.instant('COST_REVIEW_ADDITIONAL_SYSTEMS'),
            item,
            systemAttributesDataRows,
            CONSTANTS.ADDITIONAL_SYSTEMS
          );
        });
      }
    } else {
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        systemAttributesDataRows
      )
    }

    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_SYSTEM_ATTRIBUTES'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: systemAttributesDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateSystemConfigurationTable(systemConfiguration: CostReviewFireEnUsSystemConfiguration): void {
    let systemConfigurationDataRows: IData[] = [];

    if (systemConfiguration?.isSystemConfigurationApplicable) {
      if (systemConfiguration.desigoPanels && systemConfiguration.desigoPanels.length > 0) {
        const sortedDesigoPanels = [...systemConfiguration.desigoPanels].sort((a, b) => a - b);
        sortedDesigoPanels.forEach(item => {
          this.createRowOneValueData(
            this.translate.instant('COST_REVIEW_DESIGO_PANELS'),
            item,
            systemConfigurationDataRows,
            CONSTANTS.DESIGO_PANELS
          );
        });
      }
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_VOICE_SYSTEM'),
        systemConfiguration.voiceSystem,
        systemConfigurationDataRows,
        CONSTANTS.YES_NO
      );
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_GLOBAL_VOICE'),
        systemConfiguration.globalVoice,
        systemConfigurationDataRows,
        CONSTANTS.YES_NO
      );
      if (systemConfiguration.backupAmps && systemConfiguration.backupAmps.length > 0) {
        const sortedBackupAmps = [...systemConfiguration.backupAmps].sort((a, b) => a - b);
        sortedBackupAmps.forEach(item => {
          this.createRowOneValueData(
            this.translate.instant('COST_REVIEW_BACKUP_AMPS'),
            item,
            systemConfigurationDataRows,
            CONSTANTS.BACKUP_AMPS
          )
        });
      }

      const systemConfigFieldsMap: ChecklistReportGenericFieldMap<CostReviewFireEnUsSystemConfiguration>[] = [
        { label: this.translate.instant('COST_REVIEW_BATTERY_BACKUP'), key: 'batteryBackup', parameterId: CONSTANTS.BATTERY_BACKUP },
        { label: this.translate.instant('COST_REVIEW_NAC_WIRING'), key: 'nacWiring', parameterId: CONSTANTS.CLASS_A_B },
        { label: this.translate.instant('COST_REVIEW_IDC_WIRING'), key: 'idcWiring', parameterId: CONSTANTS.CLASS_A_B },
        { label: this.translate.instant('COST_REVIEW_SLC_WIRING'), key: 'slcWiring', parameterId: CONSTANTS.CLASS_A_B_X },
        { label: this.translate.instant('COST_REVIEW_PROPOSED_SYSTEM_MEETS_SPECS'), key: 'proposedSystem', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_DIALER_PROVIDED_BY_SIEMENS'), key: 'dialerProvidedBySiemens', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_COMMUNICATION_METHOD'), key: 'communicationMethod', parameterId: CONSTANTS.COMMUNICATION_METHOD },
        { label: this.translate.instant('COST_REVIEW_MXLV_DESIGO_FSM_MIGRATION'), key: 'mxlvDesigoFsmMigration', parameterId: CONSTANTS.YES_NO }
      ];

      systemConfigFieldsMap.forEach(field => {
        const value = systemConfiguration[field.key];
        if (value !== undefined && value !== null) {
          const displayValue = this.getParameterValue(field.parameterId, value);
          if (displayValue && (field.key !== 'batteryBackup' || displayValue.trim())) {
            this.createRowOneValueData(
              this.translate.instant(field.label),
              displayValue,
              systemConfigurationDataRows
            )
          }
        }
      });

      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_REQUIRED_NETWORK'),
        '',
        systemConfigurationDataRows
      );

      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_NUMBER_OF_NODES'),
        systemConfiguration.numberOfNodes.toString(),
        systemConfigurationDataRows
      )

      if (systemConfiguration.numberOfNodes > 0) {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_NETWORK_STYLE'),
          systemConfiguration.networkStyle,
          systemConfigurationDataRows,
          CONSTANTS.NETWORK_STYLE
        )

        if (systemConfiguration.networkOptions && systemConfiguration.networkOptions.length > 0) {
          const sortedNetworkOptions = [...systemConfiguration.networkOptions].sort((a, b) => a - b);
          sortedNetworkOptions.forEach(item => {
            this.createRowOneValueData(
              this.translate.instant('COST_REVIEW_NETWORK_STYLE'),
              item,
              systemConfigurationDataRows,
              CONSTANTS.NETWORK_OPTIONS
            )
          });
        }
      }
    } else {
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        systemConfigurationDataRows
      )
    }

    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_SYSTEM_CONFIGURATION'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: systemConfigurationDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateSystemInterfacesTable(systemInterfaces: CostReviewFireEnUsSystemInterfaces): void {
    let systemInterfacesDataRows: IData[] = [];

    if (systemInterfaces?.isSystemInterfacesApplicable) {
      if (systemInterfaces.systemInterfaces && systemInterfaces.systemInterfaces.length > 0) {
        const sortedSystemInterfaces = [...systemInterfaces.systemInterfaces].sort((a, b) => a - b);

        sortedSystemInterfaces.forEach(item => {
          this.createRowWithNumericLabel(
            item,
            CONSTANTS.SYSTEM_INTERFACES,
            this.translate.instant('COST_REVIEW_INCLUDED'),
            systemInterfacesDataRows
          )

          // Add indented sub-items based on the interface type
          this.fillIndentedInterfaceSubItems(
            systemInterfaces,
            systemInterfacesDataRows,
            item
          );
        });
        this.createRowOneValueData(
      '  ' + this.translate.instant('COST_REVIEW_OTHER_NOTES'),
      systemInterfaces.otherNotes,
      systemInterfacesDataRows
    );
      }
    } else {
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        systemInterfacesDataRows
      )
    }

    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_SYSTEM_INTERFACES'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: systemInterfacesDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  private fillIndentedInterfaceSubItems(
    systemInterfaces: CostReviewFireEnUsSystemInterfaces,
    systemInterfacesDataRows: IData[],
    currentItem: number
  ): void {
    const FIRE_SMOKE_DAMPERS = 1;
    const LED_ANNUNCIATORS = 2;
    const ALPHA_NUMERIC = 3;
    if (currentItem === FIRE_SMOKE_DAMPERS) {
      if (systemInterfaces.fireSmokeDampers && systemInterfaces.fireSmokeDampers.length > 0) {
        const sortedFireSmokeDampers = [...systemInterfaces.fireSmokeDampers].sort((a, b) => a - b);

        sortedFireSmokeDampers.forEach(item => {
          this.createRowOneValueData(
            '  ' + item,
            this.translate.instant('COST_REVIEW_INCLUDED'),
            systemInterfacesDataRows,
            CONSTANTS.FIRE_SMOKE_DAMPERS_PARAMETER
          )
        });
      }
    }
    if (currentItem === LED_ANNUNCIATORS) {
      if (systemInterfaces.ledAnnunciator && systemInterfaces.ledAnnunciator.length > 0) {
        const sortedLedAnnunciator = [...systemInterfaces.ledAnnunciator].sort((a, b) => a - b);

        sortedLedAnnunciator.forEach(item => {
          this.createRowOneValueData(
            '  ' + item,
            this.translate.instant('COST_REVIEW_INCLUDED'),
            systemInterfacesDataRows,
            CONSTANTS.LED_ANNUNCIATOR
          )
        });
      }
    }

    if (currentItem === ALPHA_NUMERIC) {
      if (systemInterfaces.alphaNumericAnnunciator && systemInterfaces.alphaNumericAnnunciator.length > 0) {
        const sortedAlphaNumericAnnunciator = [...systemInterfaces.alphaNumericAnnunciator].sort((a, b) => a - b);

        sortedAlphaNumericAnnunciator.forEach(item => {
          this.createRowOneValueData(
            '  ' + item,
            this.translate.instant('COST_REVIEW_INCLUDED'),
            systemInterfacesDataRows,
            CONSTANTS.ALPHA_NUMERIC_ANNUNCIATOR
          );
        });
      }
    }
  }

  generateSystemExecutionScheduleTable(systemExecutionSchedule: CostReviewFireEnUsExecutionSchedule): void {
    let executionScheduleDataRows: IData[] = [];

    if (systemExecutionSchedule?.isExecutionScheduleChecked) {
      // Map for Execution Schedule fields
      const executionScheduleFieldsMap: ChecklistReportGenericFieldMap<CostReviewFireEnUsExecutionSchedule>[] = [
        { label: this.translate.instant('COST_REVIEW_PHASED_INSTALLATION_OCCUPANCY'), key: 'phasedInstallationOccupancy', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_PROJECT_SCHEDULE_PUBLISHED'), key: 'projectSchedulePublished', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_FIRE_PLAN_IDENTIFIED'), key: 'firePlanIdentified', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_LOCAL_OFFICE_TECHNICAL_COMPETENCIES'), key: 'localOfficeTechnicalCompetencies', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_LOCAL_OFFICE_STAFFED_FOR_WORK'), key: 'localOfficeStaffedForWork', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SPECIAL_COMMISSIONING_PROCEDURES'), key: 'specialCommissioningProceduresRequired', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_DESIGN_CONSIDERATIONS'), key: 'designConsiderations', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_UNION_PROJECT'), key: 'unionProject', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SPECIAL_TAGGING_REQUIREMENTS'), key: 'specialTaggingRequirements', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_RESPONSIBLE_FOR_HAZARDOUS_MATERIALS'), key: 'responsibleForHazardousMaterials', parameterId: CONSTANTS.SIEMENS_OTHER },
        { label: this.translate.instant('COST_REVIEW_CONFINED_WORKSPACE'), key: 'confinedWorkSpace', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_RESPONSIBLE_FOR_INACCESSIBLE_ACCESS'), key: 'responsibleForInaccessibleAccess', parameterId: CONSTANTS.SIEMENS_OTHER }
      ];

      executionScheduleFieldsMap.forEach(field => {
        const value = systemExecutionSchedule[field.key];
        this.createRowOneValueData(
          field.label,
          value,
          executionScheduleDataRows,
          field.parameterId
        )
      });
    } else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        executionScheduleDataRows
      )
    }

    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_EXECUTION_SCHEDULE'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: executionScheduleDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateOutsidePurchasesTable(outsidePurchases: CostReviewFireEnUsOutsidePurchases): void {
    let outsidePurchasesDataRows: IData[] = [];

    if (outsidePurchases?.isOutsidePurchasesApplicable) {
      // Map for Outside Purchases fields
      const outsidePurchasesFieldsMap: ChecklistReportGenericFieldMap<CostReviewFireEnUsOutsidePurchases>[] = [
        { label: this.translate.instant('COST_REVIEW_SPECIAL_FABRICATION_ITEMS'), key: 'specialFabricationItems', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_HEATERS_REQUIRED'), key: 'heatersRequired', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_THIRD_PARTY_MATERIALS_IDENTIFIED'), key: 'thirdPartyMaterialsIdentified', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SUPPLIER_PART_NUMBERS'), key: 'supplierPartNumbers', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_OUTSIDE_PURCHASE_SYSTEMS'), key: 'outsidePurchaseSystems', parameterId: CONSTANTS.YES_NO }
      ];

      outsidePurchasesFieldsMap.forEach(field => {
        const value = outsidePurchases[field.key];
        this.createRowOneValueData(
          field.label,
          value,
          outsidePurchasesDataRows,
          field.parameterId
        )
      });

      // If Outside Purchase Systems = YES (1), add follow-up question
      if (outsidePurchases.outsidePurchaseSystems === 1) {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_IF_YES_REQUIRE_MANUFACTURER'),
          outsidePurchases.ifYesRequireManufacturer,
          outsidePurchasesDataRows,
          CONSTANTS.YES_NO
        )
      }
    } else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        outsidePurchasesDataRows
      )
    }

    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_OUTSIDE_PURCHASES'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: outsidePurchasesDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateRetrofitTableFire(retrofitConsiderations: CostReviewFireEnUsRetrofitConsiderations): void {
    const retrofitDataRows: IData[] = [];

    if (retrofitConsiderations?.isRetrofitConsiderationsApplicable) {
      const retrofitFieldsMap: ChecklistReportGenericFieldMap<CostReviewFireEnUsRetrofitConsiderations>[] = [
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.SYSTEM_COMPONENT_REUSE'), key: 'systemComponentReuse', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.EXCEPTIONS'), key: 'exceptions' },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.EXISTING_SYSTEM_MANUFACTURER'), key: 'existingSystemManufacturer' },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.EXISTING_SYSTEM_MODEL'), key: 'existingSystemModel' },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.EXISTING_SYSTEM_OPERATIONAL_DURING_RETROFIT'), key: 'operationalDuringRetrofit', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.SUFFICIENT_SPACE_TO_MOUNT_NEW_ENCLOSURES'), key: 'sufficientSpaceToMount', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.CONDITIONS_VERIFIED'), key: 'conditionsVerified', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.EXISTING_WIRING_REUSED'), key: 'existingWiringReused', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.SUBCONTRACTOR_AWARE'), key: 'subcontractorIsAware', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.WIRING_COMPATIBLE_WITH_NEW_SYSTEM'), key: 'wiringIsCompatible', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.CONDUIT_OR_RACEAWAY_REUSE'), key: 'conduitOrRacewayReuse', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.RETROFIT_CONSIDERATIONS.FIRE_WATCH_REQUIRED'), key: 'fireWatchRequired', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_RESPONSIBLE_FOR_COMPONENT_AND_WIRING_TESTING'), key: 'responsibleForComponent', parameterId: CONSTANTS.SIEMENS_OTHER }
      ];

      retrofitFieldsMap.forEach(field => {
        const value = retrofitConsiderations[field.key];
        this.createRowOneValueData(
          field.label,
          value,
          retrofitDataRows,
          field.parameterId
        );
      });
    } else {
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        retrofitDataRows
      );
    }

    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_RETROFIT_REQUIREMENTS'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: retrofitDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateOperatorWorkstationAndSoftwareTableFire(operatorWorkstationData: CostReviewOperatorWorkstationSoftware): void {
    const operatorWorkstationDataRows: IData[] = [];

    if (operatorWorkstationData?.isOpWorksationsSoftwareApplicable) {
      const operatorWorkstationFieldsMap: ChecklistReportGenericFieldMap<CostReviewOperatorWorkstationSoftware>[] = [
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.NUMBER_OF_SERVERS'), key: 'numberOfServers' },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.NUMBER_OF_WORKSTATIONS'), key: 'numberOfWorkstations' },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.NUMBER_OF_CLIENTS'), key: 'numberOfClients' },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.COMPETITOR_SYSTEM'), key: 'competitorSystem' },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.INTERNET_ACCESSIBLE'), key: 'internetAccessible', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.FEATURES_REQUIRED'), key: 'featuresRequired' },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.NUMBER_OF_GRAPHICS'), key: 'numberOfGraphics' },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.CENTRALIZED_GRAPHICS_UTILIZED'), key: 'centralizedGraphicsUtilized', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.GECC_UTILIZED'), key: 'geccUtilized', parameterId: CONSTANTS.YES_NO }
      ];

      operatorWorkstationFieldsMap.forEach(field => {
        const value = operatorWorkstationData[field.key];
        this.createRowOneValueData(
          field.label,
          value,
          operatorWorkstationDataRows,
          field.parameterId
        );
      });

      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_NETWORK_REQUIREMENTS'),
        '',
        operatorWorkstationDataRows
      );

      if (operatorWorkstationData.protocols && operatorWorkstationData.protocols.length > 0) {
        const sortedProtocols = [...operatorWorkstationData.protocols].sort((a, b) => a - b);
        sortedProtocols.forEach(item => {
          this.createRowOneValueData(
            this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.PROTOCOLS'),
            item,
            operatorWorkstationDataRows,
            'PROTOCOLS'
          );
        });
      }

      this.createRowOneValueData(
        this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.NUMBER_OF_SWITCHES_HUBS'),
        operatorWorkstationData.numberOfSwitches,
        operatorWorkstationDataRows
      );
    } else {
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        operatorWorkstationDataRows
      );
    }

    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_OPERATOR_WORKSTATION_AND_SOFTWARE'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        }
      ],
      data: operatorWorkstationDataRows,
      tableFooter: [],
      isPageBreakAfter: true,
      isTableBordered: true
    });
  }

  generateElectricalWiringTableFire(electricalWiring: CostReviewElectricalWiring, wiringResponsibilities: CostReviewFireEnUsWiringResponsibilities): void {
    const electricalWiringDataRows: IData[] = [];

    if (electricalWiring?.isElectricalWiringApplicable) {
      const electricalWiringFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalWiring>[] = [
        { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SUBCONTRACTOR'), key: 'electricalSubcontractor' },
        { label: this.translate.instant('COST_REVIEW_AMOUNT_BUDGETED'), key: 'amountBudgeted' },
        { label: this.translate.instant('COST_REVIEW_IF_SUB_50K_3BIDS_SECURED'), key: 'threeBidsSecured', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_REQUESTED_QUOTES_FROM'), key: 'requestedQuotesFrom' },
        { label: this.translate.instant('COST_REVIEW_SUB_HAS_PROJECT_SPECIFICATIONS'), key: 'subHasProjectSpecifications', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SUB_HAS_CAPACITY_TO_COMPLETE_WORK'), key: 'subHasCapacityToCompleteWork', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SUB_CARRIES_BOND'), key: 'subCarriesBond', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_CONTRACT_VALUE'), key: 'contractValue' },
        { label: this.translate.instant('COST_REVIEW_CONDUIT_TYPE'), key: 'conduitType', parameterId: CONSTANTS.CONDUIT_TYPE },
        { label: this.translate.instant('COST_REVIEW_ALL_CONDUIT_REQUIRED'), key: 'allConduitRequired', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_MINIMUM_CONDUIT_SIZE'), key: 'minimumConduitSize', parameterId: 'MINIMUM_CONDUIT_SIZE' },
        { label: this.translate.instant('COST_REVIEW_SAFETIES_FURNISHED_BY'), key: 'safetiesFurnishedBy' },
        { label: this.translate.instant('COST_REVIEW_CONTRACTOR_SUBCONTRACTOR_HAS_DPU'), key: 'contractorSubcontractorHasDPU', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_IF_NO_WILL_ONE_BE_PROVIDED'), key: 'ifNoWillOneBeProvided', parameterId: CONSTANTS.YES_NO }
      ];

      electricalWiringFieldsMap.forEach(field => {
        const value = electricalWiring[field.key];
        this.createRowOneValueData(
          field.label,
          value,
          electricalWiringDataRows,
          field.parameterId
        );
      });

      if (this.estimateType === EstimateType.EstimateGreater150K) {
        const estimateGreater150KFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalWiring>[] = [
          { label: this.translate.instant('COST_REVIEW_FPM_INVOLVED'), key: 'fpmInvolved', parameterId: CONSTANTS.YES_NO },
          { label: this.translate.instant('COST_REVIEW_QUOTE_VARIANCE'), key: 'quoteVariance' },
          { label: this.translate.instant('COST_REVIEW_RECEIVED_BINDING_QUOTE'), key: 'receivedBindingQuote', parameterId: CONSTANTS.YES_NO }
        ];

        estimateGreater150KFieldsMap.forEach(field => {
          const value = electricalWiring[field.key];
          this.createRowOneValueData(
            field.label,
            value,
            electricalWiringDataRows,
            field.parameterId
          );
        });
      }

      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SUB_CARRIES_BOND_REASON'),
        electricalWiring.subCarriesBondReason,
        electricalWiringDataRows
      );

      if (wiringResponsibilities?.isWiringResponsibilitiesApplicable) {
        const wiringResponsibilitiesFieldsMap: ChecklistReportGenericFieldMap<CostReviewFireEnUsWiringResponsibilities>[] = [
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FIELD_TERMINATIONS'), key: 'fieldTerminations', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.PANEL_TERMINATIONS'), key: 'panelTerminations', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.PAD_AND_REMOTE_ANNUNCIATOR'), key: 'padAndRemoteAnnunciator', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.HIGH_VOLTAGE_WIRING'), key: 'highVoltageWiring', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.SUPPLY_24VDC'), key: 'supply24Vdc', parameterId: CONSTANTS.YES_NO },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.THIRD_PARTY_TERMINATIONS'), key: 'thirdPartyTerminations', parameterId: CONSTANTS.YES_NO },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FIBER_OPTICS_REQUIRED'), key: 'fiberOpticsRequired', parameterId: CONSTANTS.YES_NO },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FIBER_FURNISHED_BY'), key: 'fiberFurnishedBy', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FIBER_INSTALLED_BY'), key: 'fiberInstalledBy', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FIBER_TERMINATIONS_BY'), key: 'fiberTerminationsBy', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.DUCT_HOUSINGS_AND_SAMPLING'), key: 'ductHousingsAndSampling', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.PROVIDES_POWER_FOR_ENCLOSURE_HEATERS'), key: 'providesPowerForEnclosureHeaters', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FIRE_SMOKE_DAMPER_VALVE'), key: 'fireSmokeDamperValve', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.POWER_FOR_ACTUATORS_FURNISHED_BY'), key: 'powerForActuatorsFurnishedBy', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.WIRE_CABLING_FURNISHED_BY'), key: 'wireCablingFurnishedBy', parameterId: CONSTANTS.SIEMENS_OTHER },
          { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FREE_RUN_CABLE_ACCEPTABLE'), key: 'freeRunCableAcceptable', parameterId: CONSTANTS.YES_NO }
        ];

        wiringResponsibilitiesFieldsMap.forEach(field => {
          const value = wiringResponsibilities[field.key];
          this.createRowOneValueData(
            field.label,
            value,
            electricalWiringDataRows,
            field.parameterId
          );
        });
      }
    } else {
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        electricalWiringDataRows
      );
    }

    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_ELECTRICAL_WIRING_CONTROLS'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: electricalWiringDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  //#endregion

  //#region Cost review SECURITY Mappings

  generateGeneralConditionsTableSecurity(generalSecurityConditions: CostReviewSecurityEnUsGeneralConditions, generalCondition: CostReviewGeneralConditions): void {
    let generalConditionsDataRows: IData[] = [];
    if (generalSecurityConditions?.isGeneralConditionChecked || generalCondition?.isGeneralConditionsApplicable) {
      const isEstimateGreaterThan150K = this.estimateType === EstimateType.EstimateGreater150K;
      const isCpmVisible = this.approvedSellPrice > CONSTANTS.ONE_MILLION;

      if (isCpmVisible) {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_CPM_HOURS_ESTIMATED'),
          generalCondition?.cpmHoursEstimated,
          generalConditionsDataRows,
          CONSTANTS.YES_NO
        );
      }

      const securityFieldsMap: ChecklistReportGenericFieldMap<CostReviewSecurityEnUsGeneralConditions>[] = [
        { label: this.translate.instant('COST_REVIEW_SPARE_HARDWARE_SOFTWARE_CAPACITY_REQUIRED'), key: 'hardwareSoftwareCapacity', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SERVICE_CONTRACT_REQUIRED'), key: 'serviceContract', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SPECIAL_COMMISSIONING_PROCEDURES'), key: 'commissioningProcedures', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SPECIAL_TAGGING_LABELING_REQUIRED'), key: 'taggingLabeling', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SPECIAL_INSTALLATION_STANDARDS_REQUIRED'), key: 'installationStandards', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_LOCAL_CODE_RESTRICTIONS_DOOR_HARDWARE'), key: 'localCodeRestrictions', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_ESCORTS_REQUIRED_WORK_AREAS'), key: 'escortsRequired', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_CUSTOMER_TAX_EXEMPT'), key: 'customerTaxExempt', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_OVERTIME_PWR_ESTIMATED'), key: 'overtimeEstimated' },
        { label: this.translate.instant('COST_REVIEW_ESCALATION_INCLUDED'), key: 'escalationIncluded' },
        { label: this.translate.instant('COST_REVIEW_UNION_LABOR_REQUIRED'), key: 'unionLabor', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_PREVAILING_WAGES_REQUIRED'), key: 'prevailingWages', parameterId: CONSTANTS.YES_NO }
      ];

      securityFieldsMap.forEach(field => {
        this.createRowOneValueData(
          field.label,
          generalSecurityConditions[field.key],
          generalConditionsDataRows,
          field.parameterId
        );
      });

      if (isEstimateGreaterThan150K) {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_SPECIAL_PROGRAMMING_REQUIRED'),
          generalSecurityConditions.specialProgramming,
          generalConditionsDataRows,
          CONSTANTS.YES_NO
        );
      }

      if ((generalSecurityConditions.specialProgramming === CONSTANTS.ONE || isEstimateGreaterThan150K) && generalSecurityConditions.ifYesDescribe) {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_IF_YES_DESCRIBE'),
          generalSecurityConditions.ifYesDescribe,
          generalConditionsDataRows
        );
      }

      if (generalSecurityConditions.commissioningProcedures === CONSTANTS.ONE) {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_IF_YES_TEST_PROCESS_REVIEWED'),
          generalSecurityConditions.testProcessReviewed,
          generalConditionsDataRows,
          CONSTANTS.YES_NO
        );
      }

      if (generalSecurityConditions.installationStandards === CONSTANTS.ONE) {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_IF_YES_INSTALLATION_STANDARDS_DESCRIBE'),
          generalSecurityConditions.ifYesInstallationStandards,
          generalConditionsDataRows
        );
      }

      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_PROJECT_IS_PHASED'),
        generalCondition?.projectIsPhased,
        generalConditionsDataRows,
        CONSTANTS.YES_NO
      );

      if (generalCondition?.projectIsPhased === CONSTANTS.ONE && generalCondition?.ifYesDescribe) {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_IF_YES_DESCRIBE'),
          generalCondition?.ifYesDescribe,
          generalConditionsDataRows
        );
      }

      if (generalSecurityConditions.prevailingWages === CONSTANTS.ONE) {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_IF_YES_CERTIFIED_PAYROLL_REPORTS_REQUIRED'),
          generalSecurityConditions.certifiedPayrollReports,
          generalConditionsDataRows,
          CONSTANTS.YES_NO
        );
      }

      if (isEstimateGreaterThan150K) {
        this.addCurrencyRow(
          this.translate.instant('COST_REVIEW_TRAVEL_EXPENSES_PLANNED'),
          generalCondition?.travelExpensesPlanned,
          generalConditionsDataRows
        );
      }
    } else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        generalConditionsDataRows
      );
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_GENERAL_CONDITIONS'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: generalConditionsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });

  }


  generateExecutionScheduleTableSecurity(systemExecutionSchedule: CostReviewSecurityEnUsExecutionSchedule): void {
    let executionScheduleDataRows: IData[] = [];

    if (systemExecutionSchedule?.isExecutionScheduleChecked) {
      // Map for Execution Schedule fields
      const executionScheduleFieldsMap: ChecklistReportGenericFieldMap<CostReviewSecurityEnUsExecutionSchedule>[] = [
        { label: this.translate.instant('COST_REVIEW_PHASED_INSTALLATION_OCCUPANCY'), key: 'phasedInstallation', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_DESCRIBE_PHASING_CONSIDERATIONS'), key: 'describePhasingConsiderations' },
        { label: this.translate.instant('COST_REVIEW_PROJECT_SCHEDULE_PUBLISHED'), key: 'projectSchedulePublished', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_LOCAL_OFFICE_TECHNICAL_COMPETENCIES'), key: 'officeTechnicalCompetencies', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_LOCAL_OFFICE_STAFFED_FOR_WORK'), key: 'localOfficeStaffed', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SPECIAL_TAGGING_REQUIREMENTS'), key: 'specialTaggingRequirements', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_SPECIAL_CUTOVER_REQUIREMENTS'), key: 'specialCutoverRequirements', parameterId: CONSTANTS.YES_NO }
      ];

      executionScheduleFieldsMap.forEach(field => {
        const value = systemExecutionSchedule[field.key];
        this.createRowOneValueData(
          field.label,
          value,
          executionScheduleDataRows,
          field.parameterId
        );
      });
    } else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        executionScheduleDataRows
      );
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_EXECUTION_SCHEDULE'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: executionScheduleDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateNetworkTableSecurity(networkRequirements: CostReviewSecurityEnUsNetwork): void {
    let networkRequirementsDataRows: IData[] = [];
    if (networkRequirements?.isNetworkChecked) {
      const networkFieldsMap: ChecklistReportGenericFieldMap<CostReviewSecurityEnUsNetwork> =
        { label: this.translate.instant('COST_REVIEW_NETWORK_STYLE'), key: 'networkStyle', parameterId: CONSTANTS.NETWORK_STYLE };

      const value = networkRequirements[networkFieldsMap.key];
      if (value !== undefined && value !== null) {
        this.createRowOneValueData(
          networkFieldsMap.label,
          value,
          networkRequirementsDataRows,
          networkFieldsMap.parameterId
        );
      }
      networkRequirements.networkOptions.forEach((item: number) => {
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_NETWORK_STYLE'),
          item,
          networkRequirementsDataRows,
          CONSTANTS.NETWORK_OPTIONS
        )
      });

    } else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        networkRequirementsDataRows
      );
    }

    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_NETWORK'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: networkRequirementsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateRetrofitTableSecurity(retrofitRequirements: CostReviewSecurityEnUsRetrofitConsiderations): void {
    let retrofitRequirementsDataRows: IData[] = [];
    if (retrofitRequirements?.isRetrofitConsiderationsChecked) {
      const retrofitFieldsMap: ChecklistReportGenericFieldMap<CostReviewSecurityEnUsRetrofitConsiderations>[] = [
        { label: this.translate.instant('COST_REVIEW_REUSE_OF_SYSTEM_COMPONENTS'), key: 'reuseSystemComponents', parameterId: CONSTANTS.YES_NO },
        { label: this.translate.instant('COST_REVIEW_EXISTING_WIRING_CAN_BE_REUSED'), key: 'existingWiringReused', parameterId: CONSTANTS.YES_NO }
      ];
      retrofitFieldsMap.forEach(field => {
        const value = retrofitRequirements[field.key];
        this.createRowOneValueData(
          field.label,
          value,
          retrofitRequirementsDataRows,
          field.parameterId
        );
      });
    }
    else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        retrofitRequirementsDataRows
      );
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_RETROFIT_REQUIREMENTS'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: retrofitRequirementsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateAccessControlTableSecurity(accessControlRequirements: CostReviewSecurityEnUsAccessControlSystems): void {
    let accessControlRequirementsDataRows: IData[] = [];
    if (accessControlRequirements?.isAccessSystemChecked) {
      const accessControlFieldsMap: ChecklistReportGenericFieldMap<CostReviewSecurityEnUsAccessControlSystems>[] = [
        { label: this.translate.instant('COST_REVIEW_INTEGRATION_WITH_OTHER_SYSTEMS'), key: 'integrationWithOtherSystems', parameterId: CONSTANTS.YES_NO },  //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_NEW'), key: 'newVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_REPLACEMENT'), key: 'replacementVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_EXPANSION_VENDOR'), key: 'expansionVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_DETAILED_DRAWINGS_INCLUDED_IN_RFQ'), key: 'detailedDrawings', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_FIRM_ELECTRICAL_QUOTE'), key: 'firmElectricalQuote', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_NETWORK_PROVIDED_BY'), key: 'networkProvidedBy', parameterId: CONSTANTS.SIEMENS_CUSTOMER }, //getParameterValues(constants.SIEMENS_CUSTOMER)
        { label: this.translate.instant('COST_REVIEW_SERVER_PROVIDED_BY'), key: 'serverProvidedBy', parameterId: CONSTANTS.SIEMENS_CUSTOMER }, //getParameterValues(constants.SIEMENS_CUSTOMER)
        { label: this.translate.instant('COST_REVIEW_INTEGRATION_WITH_OTHER_SYSTEMS'), key: 'clientComputer', parameterId: CONSTANTS.SIEMENS_CUSTOMER },  //getParameterValues(constants.SIEMENS_CUSTOMER)
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_CLIENTS'), key: 'numberOfClients', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_RISK'), key: 'risk', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_PANELS'), key: 'numberOfPanels', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_READERS'), key: 'numberOfReaders', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_CONTACTS'), key: 'numberOfContacts', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_REXS'), key: 'numberOfREX', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_DOOR_HARDWARE_TYPE'), key: 'doorHardwareType', parameterId: CONSTANTS.DOOR_HARDWARE_TYPE }, //getParameterValuesForArray(constants.DOOR_HARDWARE_TYPE)
        { label: this.translate.instant('COST_REVIEW_DOOR_HARDWARE_PROVIDED_BY'), key: 'doorHardwareProvidedBy', parameterId: CONSTANTS.SIEMENS_SUBCONTRACTOR_OTHER }, //getParameterValues(constants.SIEMENS_SUBCONTRACTOR_OTHER)
        { label: this.translate.instant('COST_REVIEW_DOOR_HARDWARE_INSTALLED_BY'), key: 'doorHardwareInstalledBy', parameterId: CONSTANTS.SIEMENS_SUBCONTRACTOR_OTHER },  //getParameterValues(constants.SIEMENS_SUBCONTRACTOR_OTHER)
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_DOORS'), key: 'numberOfDoors', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_LOCAL_DOORS_ALARMS'), key: 'localDoorAlarms', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_EMPLOYEES_TO_TRAIN'), key: 'employeesToTrain', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_LOW_CONTRACTOR'), key: 'lowContractor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_BIDDERS'), key: 'numberOfBidders', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_WIRE_SPEND_10K'), key: 'wireSpend', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_THIRD_PARTY_COMMODITY_SPEND_10K'), key: 'thirdPartyCommodity', parameterId: CONSTANTS.YES_NO } //getParameterValues(constants.YES_NO)
      ];
      accessControlFieldsMap.forEach(field => {
        const value = accessControlRequirements[field.key];
        if (field.key === 'doorHardwareType') {
          this.createRowForArrayData(field.label, value, accessControlRequirementsDataRows, field.parameterId);
        } else {
          this.createRowOneValueData(field.label, value, accessControlRequirementsDataRows, field.parameterId);
        }
      });
    }
    else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        accessControlRequirementsDataRows
      );
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_ACCESS_CONTROL_SYSTEM'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: accessControlRequirementsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateCCTVSystemTableSecurity(cctvSystemRequirements: CostReviewSecurityEnUsCctvSystems): void {
    let cctvSystemRequirementsDataRows: IData[] = [];
    if (cctvSystemRequirements?.isCCTVSystemsChecked) {
      const cctvSystemFieldsMap: ChecklistReportGenericFieldMap<CostReviewSecurityEnUsCctvSystems>[] = [
        { label: this.translate.instant('COST_REVIEW_INTEGRATION_WITH_OTHER_SYSTEMS'), key: 'integrationWithOtherSystems', parameterId: CONSTANTS.YES_NO },  //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_NEW'), key: 'newVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_REPLACEMENT'), key: 'replacementVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_EXPANSION'), key: 'expansionVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_SERVER_PROVIDED_BY'), key: 'serverProvidedBy', parameterId: CONSTANTS.SIEMENS_CUSTOMER }, //getParameterValues(constants.SIEMENS_CUSTOMER)
        { label: this.translate.instant('COST_REVIEW_STORAGE_PROVIDED_BY'), key: 'storageProvidedBy', parameterId: CONSTANTS.SIEMENS_CUSTOMER }, //getParameterValues(constants.SIEMENS_CUSTOMER)
        { label: this.translate.instant('COST_REVIEW_CLIENT_COMPUTER_PROVIDED_BY'), key: 'clientComputer', parameterId: CONSTANTS.SIEMENS_CUSTOMER }, //getParameterValues(constants.SIEMENS_CUSTOMER)
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_CLIENTS'), key: 'numberOfClients', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_INTEGRATION_REQUIRED'), key: 'integrationRequired', parameterId: CONSTANTS.INTEGRATION_REQUIRED }, //getParameterValuesForArray(constants.INTEGRATION_REQUIRED)
        { label: this.translate.instant('COST_REVIEW_LIFT_REQUIRED'), key: 'liftRequired', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_RISK'), key: 'risk', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_CONTROLLERS'), key: 'numberOfControllers', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_INSIDE_CAMERAS'), key: 'numberOfInsideCameras', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_OUTSIDE_CAMERAS'), key: 'numberOfOutsideCameras', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_PAN_AND_TILT_CAMERAS'), key: 'panAndTiltCameras', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_EXPECTED_STORAGE_TIME'), key: 'expectedStorageTime', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_ARCHIVING_MEDIA'), key: 'archivingMedia', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_EMPLOYEES_TO_TRAIN'), key: 'employeesToTrain', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_LOW_CONTRACTOR'), key: 'lowContractor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_BIDDERS'), key: 'numberOfBidders', parameterId: '' }
      ];
      cctvSystemFieldsMap.forEach(field => {
        const value = cctvSystemRequirements[field.key];
        if (field.key === 'integrationRequired') {
          this.createRowForArrayData(field.label, value, cctvSystemRequirementsDataRows, field.parameterId);
        } else {
          this.createRowOneValueData(field.label, value, cctvSystemRequirementsDataRows, field.parameterId);
        }
      });
    }
    else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        cctvSystemRequirementsDataRows,
        ''
      );
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_CCTV_SYSTEM'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: cctvSystemRequirementsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateIntercomTableSecurity(intercomRequirements: CostReviewSecurityEnUsIntercomSystem): void {
    let intercomRequirementsDataRows: IData[] = [];
    if (intercomRequirements?.isIntercomSystemChecked) {
      const intercomSystemFieldsMap: ChecklistReportGenericFieldMap<CostReviewSecurityEnUsIntercomSystem>[] = [
        { label: this.translate.instant('COST_REVIEW_INTEGRATION_WITH_OTHER_SYSTEMS'), key: 'integrationWithOtherSystems', parameterId: CONSTANTS.YES_NO },  //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_NEW'), key: 'newVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_REPLACEMENT'), key: 'replacementVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_EXPANSION'), key: 'expansionVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_RISK'), key: 'risk', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_MASTERS'), key: 'numberOfMasters', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_SUBSTATIONS'), key: 'numberOfSubstations', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_EMPLOYEES_TO_TRAIN'), key: 'employeesToTrain', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_LOW_CONTRACTOR'), key: 'lowContractor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_BIDDERS'), key: 'numberOfBidders', parameterId: '' }
      ];
      intercomSystemFieldsMap.forEach(field => {
        const value = intercomRequirements[field.key];
        this.createRowOneValueData(
          field.label,
          value,
          intercomRequirementsDataRows,
          field.parameterId
        );
      });
    }
    else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        intercomRequirementsDataRows
      );
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_INTERCOM_SYSTEM'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: intercomRequirementsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateIntrusionSystemTableSecurity(intrusionSystemRequirements: CostReviewSecurityEnUsIntrusionSystem): void {
    let intrusionSystemRequirementsDataRows: IData[] = [];
    if (intrusionSystemRequirements?.isIntrusionSystemChecked) {
      const intrusionSystemFieldsMap: ChecklistReportGenericFieldMap<CostReviewSecurityEnUsIntrusionSystem>[] = [
        { label: this.translate.instant('COST_REVIEW_INTEGRATION_WITH_OTHER_SYSTEMS'), key: 'integrationWithOtherSystems', parameterId: CONSTANTS.YES_NO },  //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_NEW'), key: 'newVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_REPLACEMENT'), key: 'replacementVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_EXPANSION'), key: 'expansionVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_MONITORING'), key: 'monitoringVendor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_DETAILED_DRAWINGS_INCLUDED_IN_RFQ'), key: 'detailedDrawings', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_LIFT_REQUIRED'), key: 'liftRequired', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_RISK'), key: 'risk', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_PANELS'), key: 'numberOfPanels', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_KEYPADS'), key: 'numberOfKeypads', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_CONTACTS'), key: 'numberOfContacts', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_MOTION_DETECTORS'), key: 'numberOfMotionDetectors', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_LOCAL_AUDIBLE_DEVICES'), key: 'localAudibleDevices', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_EMPLOYEES_TO_TRAIN'), key: 'employeesToTrain', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_LOW_CONTRACTOR'), key: 'lowContractor', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_NUMBER_OF_BIDDERS'), key: 'numberOfBidders', parameterId: '' },
      ];
      intrusionSystemFieldsMap.forEach(field => {
        const value = intrusionSystemRequirements[field.key];
        this.createRowOneValueData(
          field.label,
          value,
          intrusionSystemRequirementsDataRows,
          field.parameterId
        );
      });
    } else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        intrusionSystemRequirementsDataRows
      );
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_INTRUSION_SYSTEM'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: intrusionSystemRequirementsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  generateElectricalWiringTableSecurity(
    electricalWiring: CostReviewElectricalWiring,
    wiringResponsibilities: CostReviewFireEnUsWiringResponsibilities
  ): void {
    let electricalWiringDataRows: IData[] = [];
    const isEstimateGreaterThan150K = this.estimateType === EstimateType.EstimateGreater150K;

    const wiringResponsibilitiesFieldsMap: ChecklistReportGenericFieldMap<CostReviewFireEnUsWiringResponsibilities>[] = [
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FIELD_TERMINATIONS'), key: 'fieldTerminations', parameterId: CONSTANTS.SIEMENS_OTHER },
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.PANEL_TERMINATIONS'), key: 'panelTerminations', parameterId: CONSTANTS.SIEMENS_OTHER },
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FIBER_FURNISHED_BY'), key: 'fiberFurnishedBy', parameterId: CONSTANTS.SIEMENS_OTHER },
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FIBER_INSTALLED_BY'), key: 'fiberInstalledBy', parameterId: CONSTANTS.SIEMENS_OTHER },
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.FIBER_TERMINATIONS_BY'), key: 'fiberTerminationsBy', parameterId: CONSTANTS.SIEMENS_OTHER },
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.POWER_WIRING'), key: 'powerWiring', parameterId: CONSTANTS.SIEMENS_OTHER },
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.WIRE_CABLING_FURNISHED_BY'), key: 'wireCablingFurnishedBy', parameterId: CONSTANTS.SIEMENS_OTHER },
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.LOW_VOLTAGE_WIRING'), key: 'lowVoltageWiring', parameterId: CONSTANTS.SIEMENS_OTHER },
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.DEVICES_INSTALLED'), key: 'devicesInstalled', parameterId: CONSTANTS.SIEMENS_OTHER },
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.DEVICES_WIRED'), key: 'devicesWired', parameterId: CONSTANTS.SIEMENS_OTHER },
      { label: this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.WIRING_RESPONSIBILITIES.EQUIPMENT_INSTALLED'), key: 'equipmentInstalled', parameterId: CONSTANTS.SIEMENS_OTHER }
    ];

    const appendWiringResponsibilitiesRows = (): void => {
      if (!wiringResponsibilities?.isWiringResponsibilitiesApplicable) {
        return;
      }

      wiringResponsibilitiesFieldsMap.forEach(field => {
        const value = wiringResponsibilities[field.key];
        this.createRowOneValueData(field.label, value, electricalWiringDataRows, field.parameterId);
      });
    };

    if (electricalWiring?.isElectricalWiringApplicable) {
      const electricalWiringFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalWiring>[] = [
        { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SUBCONTRACTOR'), key: 'electricalSubcontractor', parameterId: '' }, //getParameterValues(constants.SIEMENS_SUBCONTRACTOR_OTHER)
        { label: this.translate.instant('COST_REVIEW_AMOUNT_BUDGETED'), key: 'amountBudgeted', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_IF_SUB_50K_3BIDS_SECURED'), key: 'threeBidsSecured', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_VENDOR_NAME_BID_ONE'), key: 'vendorNameBidOne', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_VENDOR_NAME_BID_TWO'), key: 'vendorNameBidTwo', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_VENDOR_NAME_BID_THREE'), key: 'vendorNameBidThree', parameterId: '' },
        { label: this.translate.instant('COST_REVIEW_SUB_HAS_PROJECT_SPECIFICATIONS'), key: 'subHasProjectSpecifications', parameterId: CONSTANTS.YES_NO },  //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_SUB_HAS_CAPACITY_TO_COMPLETE_WORK'), key: 'subHasCapacityToCompleteWork', parameterId: CONSTANTS.YES_NO },  //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_SUB_CARRIES_BOND'), key: 'subCarriesBond', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_SUBS_NOTIFIED_SECURITY'), key: 'subsNotifiedSecurity', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
        { label: this.translate.instant('COST_REVIEW_FIBER_OPTICS_REQUIRED'), key: 'fiberOpticsSecurity', parameterId: CONSTANTS.YES_NO }, //getParameterValues(constants.YES_NO)
      ];
      electricalWiringFieldsMap.forEach(field => {
        const value = electricalWiring[field.key];
        if (field.key === 'amountBudgeted' && value !== undefined && value !== null) {
          electricalWiringDataRows.push(this.createRowWithValuePrefix(field.label, value as number));
          return;
        }

        this.createRowOneValueData(field.label, value, electricalWiringDataRows, field.parameterId);
      });

      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SUB_CARRIES_BOND_REASON'),
        electricalWiring.subCarriesBondReason,
        electricalWiringDataRows
      );

      if (isEstimateGreaterThan150K) {
        if (electricalWiring.quoteVariance !== undefined && electricalWiring.quoteVariance !== null) {
          electricalWiringDataRows.push(
            this.createRowWithValuePrefix(this.translate.instant('COST_REVIEW_QUOTE_VARIANCE'), electricalWiring.quoteVariance)
          );
        }

        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_RECEIVED_BINDING_QUOTE'),
          electricalWiring.receivedBindingQuote,
          electricalWiringDataRows,
          CONSTANTS.YES_NO
        );

        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_SECURITY_PM_INVOLVED'),
          electricalWiring.securityPMInvolved,
          electricalWiringDataRows,
          CONSTANTS.YES_NO
        );

        if (electricalWiring.contractValue !== undefined && electricalWiring.contractValue !== null) {
          electricalWiringDataRows.push(
            this.createRowWithValuePrefix(this.translate.instant('COST_REVIEW_CONTRACT_VALUE'), electricalWiring.contractValue)
          );
        }

        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_CONDUIT_REQUIRED'),
          electricalWiring.allConduitRequired,
          electricalWiringDataRows,
          CONSTANTS.YES_NO
        );

        if (electricalWiring.allConduitRequired === CONSTANTS.ONE) {
          this.createRowOneValueData(
            this.translate.instant('COST_REVIEW_IF_YES_CONDUIT_TYPE'),
            electricalWiring.conduitType,
            electricalWiringDataRows,
            CONSTANTS.CONDUIT_TYPE
          );
        }
      }

      appendWiringResponsibilitiesRows();
    } else {
      appendWiringResponsibilitiesRows();

      if (electricalWiringDataRows.length === 0) {
        // Section Not Applicable
        this.createRowOneValueData(
          this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
          '',
          electricalWiringDataRows
        );
      }
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_ELECTRICAL_WIRING_CONTROLS'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: electricalWiringDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }


  generateManagementTableSecurity(managementRequirements: CostReviewSecurityEnUsManagementStation): void {
    let managementRequirementsDataRows: IData[] = [];
    if (managementRequirements?.isManagementStationChecked && managementRequirements?.managementStation) {
      const managementFieldsMap: ChecklistReportGenericFieldMap<CostReviewSecurityEnUsManagementStation>[] = [
        { label: this.translate.instant('COST_REVIEW_MANAGEMENT_STATION_TITLE'), key: 'managementStation', parameterId: '' }
      ];
      managementFieldsMap.forEach(field => {
        const value = managementRequirements[field.key];
        this.createRowOneValueData(
          field.label,
          value,
          managementRequirementsDataRows,
          field.parameterId
        );
      });
    } else {
      // Section Not Applicable
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        '',
        managementRequirementsDataRows
      );
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_MANAGEMENT_STATION_TITLE'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: managementRequirementsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });

  }

  // region Electrical Checklist Tables
  private generateElectricalChecklistTables(costReviewElectrical: CostReviewElectricalAttributes): void {
    this.generateElectricalScopeOfWorkTable(costReviewElectrical.costReviewScopeOfWork);
    this.generateElectricalDocumentationTable(costReviewElectrical.costReviewDocumentation);
    this.generateElectricalSafetyTable(costReviewElectrical.costReviewSafety);
    this.generateElectricalSiteAccessibilityTable(costReviewElectrical.costReviewSiteAccessibility);
    this.generateElectricalLaborAndMaterialsTable(costReviewElectrical.costReviewLaborAndMaterials);
    this.generateElectricalEquipmentTable(costReviewElectrical.costReviewEquipment);
    this.generateElectricalProvidedDocumentsTable(costReviewElectrical.costReviewProvidedDocuments);
    this.generateElectricalSubcontractorsTable(costReviewElectrical.costReviewSubcontractors);
    this.generateElectricalCommentsTable(costReviewElectrical.costReviewComments);
  }

  private generateElectricalScopeOfWorkTable(scopeOfWork: CostReviewElectricalScopeOfWork): void {
    const scopeOfWorkDataRows: IData[] = [];
    const scopeOfWorkFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalScopeOfWork>[] = [
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_BILL_OF_MATERIALS_REQUIRED'), key: 'bomRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_PROVIDED_BY'), key: 'bomProvidedBy', parameterId: CONSTANTS.SIEMENS_THIRD_PARTY },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_BOM_DOCUMENT_RECEIVED'), key: 'bomDocumentReceived', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_WRITTEN_SPECIFICATIONS_AVAILABLE'), key: 'writtenSpecificationsAvailable', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_SPECIFICATION_PROVIDER'), key: 'specificationProvider', parameterId: CONSTANTS.SIEMENS_THIRD_PARTY },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_SPECIFICATION_DOCUMENT_AVAILABLE'), key: 'specificationDocumentAvailable', parameterId: CONSTANTS.SIEMENS_THIRD_PARTY },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_DDR_KNOWN'), key: 'ddrKnown', parameterId: CONSTANTS.YES_NO_NA },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_ENGINEERING_REQUIRED'), key: 'engineeringRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_ENGINEERING_PROVIDED_BY'), key: 'engineeringProvidedBy', parameterId: CONSTANTS.SIEMENS_THIRD_PARTY },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_PE_STAMP_REQUIRED'), key: 'peStampRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SCOPE_TECHNICAL_SOLUTION_REVIEWED'), key: 'technicalSolutionReviewed', parameterId: CONSTANTS.YES_NO_NA }
    ];
    this.createElectricalRowsFromMap(scopeOfWork, scopeOfWorkFieldsMap, scopeOfWorkDataRows);
    this.pushElectricalChecklistTable(this.translate.instant('COST_REVIEW_ELECTRICAL_TITLE_SCOPE_OF_WORK'), scopeOfWorkDataRows);
  }

  private generateElectricalDocumentationTable(documentation: CostReviewElectricalDocumentation): void {
    const documentationDataRows: IData[] = [];
    const documentationFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalDocumentation>[] = [
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_DOCUMENTATION_AS_BUILT_DRAWINGS_PROVIDED'), key: 'asBuiltDrawingsProvided', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_DOCUMENTATION_ONE_LINE_DRAWINGS_PROVIDED'), key: 'oneLineDrawingsProvided', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_DOCUMENTATION_WIRING_DIAGRAMS_PROVIDED'), key: 'wiringDiagramsProvided', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_DOCUMENTATION_EQUIPMENT_SETTINGS_STUDIES_PROVIDED'), key: 'equipmentSettingsOrPowerSystemStudiesProvided', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_DOCUMENTATION_PHOTOGRAPHS_PROVIDED'), key: 'photographsProvided', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_DOCUMENTATION_NAMEPLATES_PROVIDED'), key: 'nameplatesProvided', parameterId: CONSTANTS.YES_NO }
    ];
    this.createElectricalRowsFromMap(documentation, documentationFieldsMap, documentationDataRows);
    if(documentation?.documentationComments) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_ELECTRICAL_COMMON_COMMENTS'), documentation?.documentationComments, documentationDataRows);
    }
    this.pushElectricalChecklistTable(this.translate.instant('COST_REVIEW_ELECTRICAL_TITLE_DOCUMENTATION'), documentationDataRows);
  }

  private generateElectricalSafetyTable(safety: CostReviewElectricalSafety): void {
    const safetyDataRows: IData[] = [];
    const safetyFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalSafety>[] = [
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SAFETY_CUSTOMER_SITE_SAFETY_REQUIRED'), key: 'customerSiteSafetyRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SAFETY_OBSERVER_REQUIRED'), key: 'safetyObserverRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SAFETY_SUPPLIED_BY'), key: 'safetyObserverSuppliedBy', parameterId: CONSTANTS.SIEMENS_CUSTOMER },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SAFETY_OUTAGE_REQUIRED'), key: 'outageRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SAFETY_TEMPORARY_POWER_REQUIRED'), key: 'temporaryPowerRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SAFETY_HAZARDOUS_MATERIALS_PRESENT'), key: 'hazardousMaterialsPresent', parameterId: CONSTANTS.YES_NO }
    ];
    this.createElectricalRowsFromMap(safety, safetyFieldsMap, safetyDataRows);
    this.pushElectricalChecklistTable(this.translate.instant('COST_REVIEW_ELECTRICAL_TITLE_SAFETY'), safetyDataRows);
  }

  private generateElectricalSiteAccessibilityTable(siteAccessibility: CostReviewElectricalSiteAccessibility): void {
    const siteAccessibilityDataRows: IData[] = [];
    const siteAccessibilityFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalSiteAccessibility>[] = [
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SITE_ACCESSIBILITY_ESCORTS_REQUIRED'), key: 'escortsRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SITE_ACCESSIBILITY_SECURITY_ACCESS_REQUIRED'), key: 'securityAccessRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SITE_ACCESSIBILITY_TIME_OF_DAY_REQUIRED'), key: 'timeOfDayRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SITE_ACCESSIBILITY_SPECIAL_CONSIDERATIONS_REQUIRED'), key: 'specialConsiderationsRequired', parameterId: CONSTANTS.YES_NO }
    ];
    this.createElectricalRowsFromMap(siteAccessibility, siteAccessibilityFieldsMap, siteAccessibilityDataRows);
    this.pushElectricalChecklistTable(this.translate.instant('COST_REVIEW_ELECTRICAL_TITLE_SITE_ACCESSIBILITY'), siteAccessibilityDataRows);
  }

  private generateElectricalLaborAndMaterialsTable(laborAndMaterials: CostReviewElectricalLaborAndMaterials): void {
    const laborAndMaterialsDataRows: IData[] = [];
    const laborAndMaterialsFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalLaborAndMaterials>[] = [
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_MANPOWER_STAFFING_IN_PLACE'), key: 'manpowerStaffingInPlace', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_TRAVEL_REQUIRED'), key: 'travelIsRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_COSTS_INCLUDED_IN_ESTIMATE'), key: 'costsIncludedInEstimate', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_MATERIAL_PRICING_DELIVERY_INCLUDED'), key: 'materialPricingAndDeliveryIncluded', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_SIEMENS_MATERIAL_PRICING_CONFIRMED'), key: 'siemensMaterialPricingConfirmed', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_SIEMENS_MATERIAL_DELIVERY_DATE_ESTIMATED'), key: 'siemensMaterialDeliveryDateEstimated', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_THIRD_PARTY_MATERIAL_PRICING_CONFIRMED'), key: 'thirdPartyMaterialPricingConfirmed', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_THIRD_PARTY_MATERIAL_DELIVERY_DATE_ESTIMATED'), key: 'thirdPartyMaterialDeliveryDateEstimated', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_WORK_HOUR_REQUIREMENTS'), key: 'workHourRequirements', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_PROGRESS_MEETINGS_REQUIRED'), key: 'progressMeetingsRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_LABOR_UNION_CONSIDERATIONS'), key: 'unionConsiderations', parameterId: CONSTANTS.YES_NO }
    ];
    this.createElectricalRowsFromMap(laborAndMaterials, laborAndMaterialsFieldsMap, laborAndMaterialsDataRows);
    this.pushElectricalChecklistTable(this.translate.instant('COST_REVIEW_ELECTRICAL_TITLE_LABOR_AND_MATERIALS'), laborAndMaterialsDataRows);
  }

  private generateElectricalEquipmentTable(equipment: CostReviewElectricalEquipment): void {
    const equipmentDataRows: IData[] = [];
    const equipmentFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalEquipment>[] = [
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_EQUIPMENT_TOOLS_TEST_EQUIPMENT_REQUIRED'), key: 'toolsAndTestEquipmentRequired', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_EQUIPMENT_RENTAL_ESTIMATED'), key: 'equipmentRentalEtcEstimated', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_EQUIPMENT_RESPONSIBLE_FOR_PROVIDING'), key: 'responsibleForProviding', parameterId: CONSTANTS.SIEMENS_THIRDPARTY_CUSTOMER }
    ];
    this.createElectricalRowsFromMap(equipment, equipmentFieldsMap, equipmentDataRows);
    const specialEquipmentList = equipment?.specialEquipmentList;
    if (specialEquipmentList?.length) {
      specialEquipmentList.forEach((value: number) => {
        const trimmedValue = value.toString().trim();
        if (trimmedValue) {
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_ELECTRICAL_EQUIPMENT_SPECIAL_EQUIPMENT_LIST'), trimmedValue, equipmentDataRows, CONSTANTS.SPECIAL_EQUIPMENT);
        }
      });
    }
    this.pushElectricalChecklistTable(this.translate.instant('COST_REVIEW_ELECTRICAL_TITLE_EQUIPMENT'), equipmentDataRows);
  }

  private generateElectricalProvidedDocumentsTable(providedDocuments: CostReviewElectricalProvidedDocuments): void {
    const providedDocumentsDataRows: IData[] = [];
    const providedDocumentsFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalProvidedDocuments>[] = [
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_PROVIDED_DOCUMENTS_BOOKING_PACKAGE_SIGNED'), key: 'bookingPackageSigned', parameterId: CONSTANTS.YES_NO },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_PROVIDED_DOCUMENTS_SCOPE_OF_WORK_REVIEWED_VALIDATED'), key: 'scopeOfWorkReviewedAndValidated', parameterId: CONSTANTS.YES_NO }
    ];
    this.createElectricalRowsFromMap(providedDocuments, providedDocumentsFieldsMap, providedDocumentsDataRows);
    this.pushElectricalChecklistTable(this.translate.instant('COST_REVIEW_ELECTRICAL_TITLE_PROVIDED_DOCUMENTS'), providedDocumentsDataRows);
  }

  private generateElectricalSubcontractorsTable(subcontractors: CostReviewElectricalSubcontractors): void {
    const subcontractorsDataRows: IData[] = [];
    const subcontractorFieldsMap: ChecklistReportGenericFieldMap<CostReviewElectricalSubcontractors>[] = [
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SUBCONTRACTORS_TERMS_CONDITIONS'), key: 'subcontractorTermsAndConditions', parameterId: CONSTANTS.YES_NO_NA },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SUBCONTRACTORS_VENDOR_PRICING_CONFIRMED'), key: 'vendorPricingConfirmed', parameterId: CONSTANTS.YES_NO_NA },
      { label: this.translate.instant('COST_REVIEW_ELECTRICAL_SUBCONTRACTORS_ADDITIONAL_MATERIAL_PRICE_DELIVERY_CONFIRMED'), key: 'additionalMaterialPriceAndDeliveryConfirmed', parameterId: CONSTANTS.YES_NO_NA }
    ];
    this.createElectricalRowsFromMap(subcontractors, subcontractorFieldsMap, subcontractorsDataRows);
    this.pushElectricalChecklistTable(this.translate.instant('COST_REVIEW_ELECTRICAL_TITLE_SUBCONTRACTORS'), subcontractorsDataRows);
  }

  private generateElectricalCommentsTable(comments: CostReviewElectricalComments): void {
    if(!comments?.comments) {
      return;
    }
    const commentsDataRows: IData[] = [];
    if (comments?.comments) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_ELECTRICAL_COMMON_COMMENTS'), comments?.comments, commentsDataRows);
      this.pushElectricalChecklistTable(this.translate.instant('COST_REVIEW_ELECTRICAL_TITLE_COMMENTS_CONCERNS'), commentsDataRows);
    }
  }

  private createElectricalRowsFromMap<TEntity>(
    source: TEntity | undefined,
    fieldsMap: ChecklistReportGenericFieldMap<TEntity>[],
    dataRows: IData[]
  ): void {
    if (!source) {
      return;
    }

    fieldsMap.forEach(field => {
      const value = source[field.key as keyof TEntity] as DataValue;
      this.createRowOneValueData(field.label, value, dataRows, field.parameterId);
    });
  }

  private pushElectricalChecklistTable(title: string, dataRows: IData[]): void {
    this.checklistTableData.push({
      title: [
        {
          data: title,
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: '',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: this.FIXED_SECOND_COLUMN_WIDTH
        },
      ],
      data: dataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  //#endregion
  createRowOneValueData(label: string, value: DataValue, dataRows: IData[], param?: string, hierarchyLevel: number = 0): void {
    //Added Or condition over here as earlier condition was not creating empty row bceause we are sending empty string and in condition we have written && value !== ''
    if ((value !== undefined && value !== null && value !== '') || (param === undefined && value === '')) {
      let result = param ? this.getParameterValue(param, value) : String(value);
      let rowData: IData = {
        data: [
          { data: label, columnspan: 1, hierarchyLevel: hierarchyLevel, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { data: result, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT }
        ]
      }
      dataRows.push(rowData);
    }
  }

  createRowForArrayData(label: string, values: DataValue, dataRows: IData[], parameterId?: string): void {
    if (values && Array.isArray(values) && values.length > 0) {
      values.forEach((item: any, index: number) => {
        const result = parameterId ? this.getParameterValuesForArray(parameterId, item) : String(item);
        const rowLabel =  label;
        const rowData: IData = {
          data: [
            { data: rowLabel, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
            { data: result, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT }
          ]
        };
        dataRows.push(rowData);
      });
    }
  }

  createRowWithNumericLabel(numericKey: number, parameterId: string, value: string, dataRows: IData[]): void {
    const label = this.getParameterValue(parameterId, numericKey) ?? String(numericKey);
    const rowData: IData = {
      data: [
        { data: label, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: value, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ]
    };
    dataRows.push(rowData);
  }

  createRowTwoValueData(
    label: string,
    primaryValue: DataValue,
    secondaryValue?: DataValue,
    parameterId?: string,
    alignment?: ReportTextAlignment): IData {
    let rowData: IData = {
      data: [
        { data: label, columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT },
        { data: parameterId ? this.modifiedReviewParameterData.get(`${parameterId}_${primaryValue}`) ?? '' : String(primaryValue ?? ''), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT },
        { data: parameterId ? this.modifiedReviewParameterData.get(`${parameterId}_${secondaryValue}`) ?? '' : String(secondaryValue ?? ''), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT }
      ]
    }
    return rowData;
  }

  /**
 * Creates a planned cost row aligned with CostReviewCheckListReportInfo structure
 * @param fieldName - Item or Labor name (corresponds to fieldName in CostReviewCheckListReportInfo)
 * @param cost - Item cost (corresponds to cost in CostReviewCheckListReportInfo)
 * @param fieldValueOne - TDC percent formatted (corresponds to fieldValueOne)
 * @param fieldValueThree - Hours for labor items (corresponds to fieldValueThree)
 */
  createPlannedCostRow(
    fieldName: string,
    cost: number,
    fieldValueOne: string,
    fieldValueThree?: string
  ): IData {
    const columns = [
      { data: fieldName, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT as ReportTextAlignment },
      { data: this.multilingualCurrencyPipe.transform(cost ?? 0), columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT as ReportTextAlignment },
      { data: `${this.decimalPrecisionPipe.transform(fieldValueOne ?? 0)}%`, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT as ReportTextAlignment }
    ];
    // Add fieldValueThree (hours) column for labor items
    if (fieldValueThree !== undefined) {
      columns.push({ data: this.decimalPrecisionPipe.transform(fieldValueThree), columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT as ReportTextAlignment });
    }
    return { data: columns };
  }

  createRowWithValuePrefix(
    label: string,
    value: number,
    alignment?: ReportTextAlignment
  ): IData {
    let rowData: IData = {
      data: [
        { data: label, columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT },
        { data: this.multilingualCurrencyPipe.transform(value), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT }
      ]
    };
    return rowData;
  }

  createRowThreeValueData(
    label: string,
    primaryValue: DataValue,
    secondaryValue: DataValue,
    ternaryValue: DataValue,
    alignment?: ReportTextAlignment
  ): IData {
    let rowData: IData = {
      data: [
        { data: label, columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT },
        { data: String(primaryValue ?? ''), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT },
        { data: String(secondaryValue ?? ''), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT },
        { data: String(ternaryValue ?? ''), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT }
      ]
    };
    return rowData;
  }

  createRowFourValueData(
    label: string,
    primaryValue: DataValue,
    secondaryValue: DataValue,
    ternaryValue: DataValue,
    quaternaryValue: DataValue,
    alignment?: ReportTextAlignment
  ): IData {
    let rowData: IData = {
      data: [
        { data: label, columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT },
        { data: String(primaryValue ?? ''), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT },
        { data: String(secondaryValue ?? ''), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT },
        { data: String(ternaryValue ?? ''), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT },
        { data: String(quaternaryValue ?? ''), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.LEFT_ALIGNMENT }
      ]
    };
    return rowData;
  }

  createFooterRow(
    label: string,
    primaryValue: DataValue,
    secondaryValue: DataValue,
    isCurrency: boolean = false
  ): IFooter {
    const primaryValueText = isCurrency && typeof primaryValue === 'number'
      ? this.multilingualCurrencyPipe.transform(primaryValue)
      : String(primaryValue ?? '');
    
    let footerRow: IFooter = {
      headers: [
        { headerText: label, columnspan: 1, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { headerText: primaryValueText, columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
        { headerText: String(secondaryValue ?? ''), columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT }
      ]
    }
    return footerRow;
  }


  //#region Automation Related Methods

  //#region Prefab Tables
  generatePrefabTable(costReviewPrefab: CostReviewPrefab): void {
    let prefabDataRows: IData[] = [];
    if(!costReviewPrefab?.isPrefabControllerVisible) {
      return;
    }

    this.createRowOneValueData(this.translate.instant('COST_REVIEW_PRE_FAB_CONTROLLER_USED'), costReviewPrefab.prefabControllerUsed, prefabDataRows, CONSTANTS.EMPTY_STRING);
    
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_PRE_FAB_CONTROLLER'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
      ],
      data: prefabDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Prefab Tables

  //#region Project Management Requirements Table
  generatePMRequirementTable(pmRequirementData: CostReviewProjectMgmtRequirements): void {
    let pmRequirementDataRows: IData[] = [];
    if (pmRequirementData?.isProjectMgmtRequirementsApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_FREQUENCY_OF_PROJECT_MEETINGS'), pmRequirementData.frequencyOfProjectMeetings, pmRequirementDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_DOCUMENTATION_REQUIREMENTS'), pmRequirementData.documentationRequirements, pmRequirementDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_TIME_ESTIMATED_TO_MANAGE_SCHEDULE'), pmRequirementData.timeEstimatedToManageSchedule, pmRequirementDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_ESTIMATED_PM_HOURS_ON_JOB_SITE'), pmRequirementData.estimatedPmHoursOnJobSite.toString(), pmRequirementDataRows, CONSTANTS.EMPTY_STRING);
    }
    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('PROJECT_MANAGEMENT_REQUIREMENTS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: pmRequirementDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Project Management Requirements Table

  //#region Building Level Network Panels Table

  generateBuildingLevelNetworkPanelTable(buildingLevelNetworkPanelData: CostReviewBuildingLevelNetworkPanels): void {
    let buildingLevelDataRows: IData[] = [];
    if (buildingLevelNetworkPanelData?.isBuildingLevelNetworkPanelsApplicable) {
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_BUILDING_LEVEL_NETWORK_PANELS'), buildingLevelNetworkPanelData.numberOfBuildingLevelNetworks, buildingLevelDataRows, CONSTANTS.EMPTY_STRING);
      }
      if (buildingLevelNetworkPanelData.fieldPanelProductName){
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_FIELD_PANEL_PRODUCT_NAME'), buildingLevelNetworkPanelData.fieldPanelProductName, buildingLevelDataRows, CONSTANTS.EMPTY_STRING);
      }
      
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_FIELD_PANEL_DRIVERS'), buildingLevelNetworkPanelData.numberOfFieldPanelDrivers, buildingLevelDataRows, CONSTANTS.EMPTY_STRING);

      if (buildingLevelNetworkPanelData.equipmentManagedByPanelDrivers) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_EQUIPMENT_MANAGED_BY_PANEL_DRIVERS'), buildingLevelNetworkPanelData.equipmentManagedByPanelDrivers, buildingLevelDataRows, CONSTANTS.EMPTY_STRING);
      }
      
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SPARE_PANEL_CAPACITY'), this.commonFunctionsService.convertToPercentage(buildingLevelNetworkPanelData.sparePanelCapacity), buildingLevelDataRows, CONSTANTS.EMPTY_STRING);
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_NETWORK_BY'), buildingLevelNetworkPanelData.networkBy, buildingLevelDataRows, 'NETWORK_BY');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_NETWORK_HARDWARE_INCLUDED'), buildingLevelNetworkPanelData.networkHardwareIncluded, buildingLevelDataRows, 'YES_NO');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_NETWORK_SPECIFICATIONS_REVIEWED'), buildingLevelNetworkPanelData.networkSpecificationsReviewed, buildingLevelDataRows, 'YES_NO');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_FIBRE_OPTIC_CABLING_REQUIRED'), buildingLevelNetworkPanelData.fiberOpticsRequired, buildingLevelDataRows, 'YES_NO');
      }
    } else {
      this.createRowOneValueData(
        this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'),
        CONSTANTS.EMPTY_STRING,
        buildingLevelDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_BUILDING_LEVEL_NETWORK_PANELS'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING,
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ],
      data: buildingLevelDataRows,
      tableFooter: [],
      isPageBreakAfter: true,
      isTableBordered: true
    });
  }
  //#endregion Building Level Network Panels Table

  //#region Operator Workstation And Software Table
  generateOperatorWorkstationAndSoftwareTable(operatorWorkstationData: CostReviewOperatorWorkstationSoftware): void {
    let operatorWorkstationDataRows: IData[] = [];
    if (operatorWorkstationData?.isOpWorksationsSoftwareApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_SERVERS'), operatorWorkstationData.numberOfServers, operatorWorkstationDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_WORKSTATIONS'), operatorWorkstationData.numberOfWorkstations, operatorWorkstationDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_CLIENTS'), operatorWorkstationData.numberOfClients, operatorWorkstationDataRows, CONSTANTS.EMPTY_STRING);
      if (operatorWorkstationData.competitorSystem) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_COMPETITOR_SYSTEM'), operatorWorkstationData.competitorSystem, operatorWorkstationDataRows, CONSTANTS.EMPTY_STRING);
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_INTERNET_ACCESSIBLE'), operatorWorkstationData.internetAccessible, operatorWorkstationDataRows, 'YES_NO');
      if (operatorWorkstationData.featuresRequired) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_FEATURES_WEB_ACCESSIBLE_REQUIRED'), operatorWorkstationData.featuresRequired, operatorWorkstationDataRows, 'SIEMENS_OTHER_SUBCONTRACTOR');
      }
      if (this.costReviewType === CostReviewType.AutomationEnUs || this.costReviewType === CostReviewType.ElectricalEnUs || this.costReviewType === CostReviewType.MechanicalEnUs) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_APT_UTILIZED'), operatorWorkstationData.aptUtilized, operatorWorkstationDataRows, 'YES_NO');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_PROGRAMS'), operatorWorkstationData.numberOfPrograms, operatorWorkstationDataRows, CONSTANTS.EMPTY_STRING);
      }      
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_GRAPHICS'), operatorWorkstationData.numberOfGraphics, operatorWorkstationDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_CENTRALIZED_GRAPHICS_UTILIZED'), operatorWorkstationData.centralizedGraphicsUtilized, operatorWorkstationDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_GECC_UTILIZED'), operatorWorkstationData.geccUtilized, operatorWorkstationDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NETWORK_REQUIREMENTS'), CONSTANTS.EMPTY_STRING, operatorWorkstationDataRows);
      operatorWorkstationData.protocols.forEach((item: number) => {
        this.createRowOneValueData(this.translate.instant('REVIEW_SOL_CHECKLIST_TAB.OPERATOR_WORKSTATION_AND_SOFTWARE.PROTOCOLS'), item, operatorWorkstationDataRows, 'PROTOCOLS', 1);
      });
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_SWITCHES_HUBS_ETC'), operatorWorkstationData.numberOfSwitches, operatorWorkstationDataRows, CONSTANTS.EMPTY_STRING, 1);
    }
    else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, operatorWorkstationDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_OPERATOR_WORKSTATION_AND_SOFTWARE'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ],
      data: operatorWorkstationDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Operator Workstation And Software Table

  //#region Chiller Plant Table
  generateChillarPlantTable(chillerPlantData: CostReviewChillerPlant): void {
    let chillerPlantDataRows: IData[] = [];
    if (chillerPlantData?.isChillerPlantApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_CHILLERS'), chillerPlantData.numberOfChillers, chillerPlantDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_PRODUCT_NAME'), chillerPlantData.manufacturerChillers, chillerPlantDataRows, CONSTANTS.EMPTY_STRING);
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_CHILLER_CONTROLS_FURNISHED_BY'), chillerPlantData.chillersControlsFurnishedBy, chillerPlantDataRows, 'SIEMENS_OTHER');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_CHILLER_CONTROLS_INSTALLED_BY'), chillerPlantData.chillersControlsInstalledBy, chillerPlantDataRows, 'SIEMENS_OTHER');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_CHILLER_CONTROLS_WIRED_BY'), chillerPlantData.chillersControlsWiredBy, chillerPlantDataRows, 'SIEMENS_OTHER');
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_DRIVERS'), chillerPlantData.driversChiller, chillerPlantDataRows, 'HARDWIRED_INTEGRATION');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_TOWERS'), chillerPlantData.numberOfTowers, chillerPlantDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_PRODUCT_NAME'), chillerPlantData.manufacturerTowers, chillerPlantDataRows, CONSTANTS.EMPTY_STRING);
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_TOWERS_CONTROLS_FURNISHED_BY'), chillerPlantData.towersControlsFurnishedBy, chillerPlantDataRows, 'SIEMENS_OTHER');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_TOWERS_CONTROLS_INSTALLED_BY'), chillerPlantData.towersControlsInstalledBy, chillerPlantDataRows, 'SIEMENS_OTHER');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_TOWERS_CONTROLS_WIRED_BY'), chillerPlantData.towersControlsWiredBy, chillerPlantDataRows, 'SIEMENS_OTHER');
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_DRIVERS'), chillerPlantData.driversTowers, chillerPlantDataRows, 'HARDWIRED_INTEGRATION');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_PUMPS'), chillerPlantData.numberOfPumps, chillerPlantDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_PRODUCT_NAME'), chillerPlantData.manufacturerPumps, chillerPlantDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_DRIVERS'), chillerPlantData.driversPumps, chillerPlantDataRows, 'HARDWIRED_INTEGRATION');
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_PUMPS_SENSORS_FURNISHED_BY'), chillerPlantData.pumpsSensorsFurnishedBy, chillerPlantDataRows, 'SIEMENS_OTHER');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_PUMPS_SENSORS_INSTALLED_BY'), chillerPlantData.pumpsSensorsInstalledBy, chillerPlantDataRows, 'SIEMENS_OTHER');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_PUMPS_SENSORS_WIRED_BY'), chillerPlantData.pumpsSensorsWiredBy, chillerPlantDataRows, 'SIEMENS_OTHER');
      }
    }
    else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, chillerPlantDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_CHILLER_PLANT'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ],
      data: chillerPlantDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Chiller Plant Table

  //#region Heating Plant Table
  generateHeatingPlantTable(heatingPlantData: CostReviewHeatingPlant): void {
    let heatingPlantDataRows: IData[] = [];
    if (heatingPlantData?.isHeatingPlantApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_BOILERS'), heatingPlantData.numberOfBoilers, heatingPlantDataRows, CONSTANTS.EMPTY_STRING);

      if (heatingPlantData.numberOfBoilers > CONSTANTS.ZERO) {
        if (heatingPlantData.manufacturerBoilers) {
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_PRODUCT_NAME'), heatingPlantData.manufacturerBoilers, heatingPlantDataRows, CONSTANTS.EMPTY_STRING);
        }
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_DRIVERS'), heatingPlantData.driversBoilers, heatingPlantDataRows, 'HARDWIRED_INTEGRATION');
        if (this.estimateType === EstimateType.EstimateGreater150K) {
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_BOILERS_CONTROLS_FURNISHED_BY'), heatingPlantData.boilersControlsFurnishedBy, heatingPlantDataRows, 'SIEMENS_OTHER');
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_BOILERS_CONTROLS_INSTALLED_BY'), heatingPlantData.boilersControlsInstalledBy, heatingPlantDataRows, 'SIEMENS_OTHER');
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_BOILERS_CONTROLS_WIRED_BY'), heatingPlantData.boilersControlsWiredBy, heatingPlantDataRows, 'SIEMENS_OTHER');
        }
      }

      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_TOWERS'), heatingPlantData.numberOfTowers, heatingPlantDataRows, CONSTANTS.EMPTY_STRING);
      
      if (heatingPlantData.numberOfTowers > CONSTANTS.ZERO) {
        if (heatingPlantData.manufacturerTowers) {
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_PRODUCT_NAME'), heatingPlantData.manufacturerTowers, heatingPlantDataRows, CONSTANTS.EMPTY_STRING);
        }
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_DRIVERS'), heatingPlantData.driversTowers, heatingPlantDataRows, 'HARDWIRED_INTEGRATION');
        if (this.estimateType === EstimateType.EstimateGreater150K) {
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_TOWERS_CONTROLS_FURNISHED_BY'), heatingPlantData.towersControlsFurnishedBy, heatingPlantDataRows, 'SIEMENS_OTHER');
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_TOWERS_CONTROLS_INSTALLED_BY'), heatingPlantData.towersControlsInstalledBy, heatingPlantDataRows, 'SIEMENS_OTHER');
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_TOWERS_CONTROLS_WIRED_BY'), heatingPlantData.towersControlsWiredBy, heatingPlantDataRows, 'SIEMENS_OTHER');
        }
      }
      
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_PUMPS'), heatingPlantData.numberOfPumps, heatingPlantDataRows, CONSTANTS.EMPTY_STRING);
      
      if (heatingPlantData.numberOfPumps > CONSTANTS.ZERO) {
        if (heatingPlantData.manufacturerPumps) {
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_PRODUCT_NAME'), heatingPlantData.manufacturerPumps, heatingPlantDataRows, CONSTANTS.EMPTY_STRING);
        }
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_DRIVERS'), heatingPlantData.driversPumps, heatingPlantDataRows, 'HARDWIRED_INTEGRATION');
        if (this.estimateType === EstimateType.EstimateGreater150K) {
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_PUMPS_SENSORS_FURNISHED_BY'), heatingPlantData.pumpsSensorsFurnishedBy, heatingPlantDataRows, 'SIEMENS_OTHER');
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_PUMPS_SENSORS_INSTALLED_BY'), heatingPlantData.pumpsSensorsInstalledBy, heatingPlantDataRows, 'SIEMENS_OTHER');
          this.createRowOneValueData(this.translate.instant('COST_REVIEW_PUMPS_SENSORS_WIRED_BY'), heatingPlantData.pumpsSensorsWiredBy, heatingPlantDataRows, 'SIEMENS_OTHER');
        }
      }
      
    } else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, heatingPlantDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_HEATING_PLANT'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ],
      data: heatingPlantDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  //#endregion Heating Plant Table


  //#region FLN Table
  generateFLNTable(flnData: CostReviewFln): void {
    let flnDataRows: IData[] = [];
    if(flnData) {
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_FLNS'), flnData.numberOfVfds, flnDataRows, CONSTANTS.EMPTY_STRING);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_PRODUCT_NAME'), flnData.productName, flnDataRows, CONSTANTS.EMPTY_STRING);
    this.createRowOneValueData(this.translate.instant('COST_REVIEW_OTHER_FLN_MSTP_DEVICES'), flnData.otherFlnMstpDevices, flnDataRows, CONSTANTS.EMPTY_STRING);
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_LEVEL_NETWORKS_FLN'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ],
      data: flnDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  }
  //#endregion FLN Table

  //#region Terminal Controller Table
  generateTerminalControllerTable(terminalControllerData: CostReviewTerminalControllers): void {
    let terminalControllerDataRows: IData[] = [];
    if (terminalControllerData?.isTerminalControllersApplicable) {
      terminalControllerData.controllerType.forEach((item: number) => {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_CONTROLLER_TYPE'), item, terminalControllerDataRows, 'CONTROLLER_TYPE');
      });
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_TERMINAL_UNITS'), terminalControllerData.numberOfTerminalUnits, terminalControllerDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_VAVS'), terminalControllerData.numberOfVavs, terminalControllerDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_OF_VAVS'), terminalControllerData.manufacturerVavs, terminalControllerDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_CUSTOM_APPLICATIONS_REQUIRED'), terminalControllerData.customApplicationsRequired, terminalControllerDataRows, 'YES_NO');
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_INSTALLED_BY'), terminalControllerData.vavsInstalledBy, terminalControllerDataRows, 'SIEMENS_OTHER');
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_FCUS'), terminalControllerData.numberOfFcus, terminalControllerDataRows);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_OF_FCUS'), terminalControllerData.manufacturerFcus, terminalControllerDataRows);
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_INSTALLED_BY'), terminalControllerData.fcusInstalledBy, terminalControllerDataRows, 'SIEMENS_OTHER');
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_UVS'), terminalControllerData.numberOfUvs, terminalControllerDataRows);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_OF_UVS'), terminalControllerData.manufacturerUvs, terminalControllerDataRows);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_FURNISHED_BY'), terminalControllerData.unitsControlsFurnishedBy, terminalControllerDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_INSTALLED_BY'), terminalControllerData.unitsControlsInstalledBy, terminalControllerDataRows, 'SIEMENS_OTHER');
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_WIRED_BY'), terminalControllerData.unitsControlsWiredBy, terminalControllerDataRows, 'SIEMENS_OTHER');
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_DRIVERS'), terminalControllerData.driversBoilers, terminalControllerDataRows, 'HARDWIRED_INTEGRATION');
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_POWER_SUPPLIED_BY'), terminalControllerData.power, terminalControllerDataRows, 'POWERTRUNK_LOCALPOWER');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_INSTALLING_PNEUMATIC_TUBING'), terminalControllerData.installingPneumaticTubing, terminalControllerDataRows, 'YES_NO');
      }
    } else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, terminalControllerDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_TERMINAL_CONTROLLERS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: terminalControllerDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Terminal Controller Table

  //#region Lab Equipment Table
  generateLabEquipmentTable(labEquipmentData: CostReviewLabEquipment): void {
    let labEquipmentDataRows: IData[] = [];
    if (labEquipmentData?.isLabEquipmentApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_TERMINAL_UNITS'), labEquipmentData.numberOfTerminalUnits, labEquipmentDataRows, CONSTANTS.EMPTY_STRING);      
      if (labEquipmentData.manufacturer) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER'), labEquipmentData.manufacturer, labEquipmentDataRows, CONSTANTS.EMPTY_STRING);
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_AIR_VALVES_FURNISHED_BY'), labEquipmentData.airValvesFurnishedBy, labEquipmentDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_AIR_VALVES_INSTALLED_BY'), labEquipmentData.airValvesInstalledBy, labEquipmentDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_CONTROLS_FURNISHED_BY'), labEquipmentData.controlsFurnishedBy, labEquipmentDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_CONTROLS_INSTALLED_BY'), labEquipmentData.controlsInstalledBy, labEquipmentDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_CONTROLS_WIRED_BY'), labEquipmentData.controlsWiredBy, labEquipmentDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NETWORK_TYPE'), labEquipmentData.networkType, labEquipmentDataRows, 'ETHERNET_MSTP');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_POWER'), labEquipmentData.power, labEquipmentDataRows, 'POWERTRUNK_LOCALPOWER');
    } else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, labEquipmentDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_LAB_EQUIPMENT'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: labEquipmentDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Lab Equipment Table

  //#region VFDs Table

  generateVFDsTable(vfdsData: CostReviewVfds): void {

    let vfdsDataRows: IData[] = [];
    if (vfdsData?.isVfdsApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_VFDS'), vfdsData.numberOfVfds, vfdsDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_PRODUCT_NAME'), vfdsData.manufacturer, vfdsDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_DRIVERS'), vfdsData.drivers, vfdsDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_FURNISHED_BY'), vfdsData.unitsFurnishedBy, vfdsDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_INSTALLED_BY'), vfdsData.unitsInstalledBy, vfdsDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_WIRED_BY'), vfdsData.unitsWiredBy, vfdsDataRows, 'SIEMENS_OTHER');
    } else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, vfdsDataRows);
    }
    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('COST_REVIEW_VARIABLE_FREQUENCY_DRIVES_VFDS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: vfdsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  //#region control valves Table

  generateControlValvesTable(controlValvesData: CostReviewControlValves): void {
    let controlValvesDataRows: IData[] = [];
    if (controlValvesData?.isControlValvesApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_CONTROL_VALVES'), controlValvesData.numberOfValves, controlValvesDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER'), controlValvesData.manufacturer, controlValvesDataRows, CONSTANTS.EMPTY_STRING);
      controlValvesData.applications.forEach((item: number) => {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_APPLICATIONS'), item, controlValvesDataRows, 'CONTROL_VALVE_APPLICATIONS');
      });
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_ACTUATORS_FURNISHED_BY'), controlValvesData.unitsActuatorsFurnishedBy, controlValvesDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_ACTUATORS_INSTALLED_BY'), controlValvesData.unitsActuatorsInstalledBy, controlValvesDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_ACTUATORS_WIRED_BY'), controlValvesData.unitsActuatorsWiredBy, controlValvesDataRows, 'SIEMENS_OTHER');
    }
    else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, controlValvesDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_CONTROL_VALVES'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ],
      data: controlValvesDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }

  //#endregion control valves Table

  //#region AFMS Table
  generateAFMSTable(afmsData: CostReviewAfms): void {
    let afmsDataRows: IData[] = [];
    if (afmsData?.isAfmsApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_AFMS'), afmsData.numberOfAfms, afmsDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER'), afmsData.manufacturer, afmsDataRows, CONSTANTS.EMPTY_STRING);
      afmsData.applications.forEach((item: number) => {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_APPLICATIONS'), item, afmsDataRows, 'AFMS_APPLICATIONS');
      });
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_AFMS_SCHEDULE'), afmsData.afmsSchedule, afmsDataRows, 'AFMS_SCHEDULE');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_DEVICES_FURNISHED_BY'), afmsData.unitsDevicesFurnishedBy, afmsDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_DEVICES_INSTALLED_BY'), afmsData.unitsDevicesInstalledBy, afmsDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_DEVICES_WIRED_BY'), afmsData.unitsDevicesWiredBy, afmsDataRows, 'SIEMENS_OTHER');
    } else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, afmsDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_AIR_FLOW_MEASUREMENT_SYSTEMS_AFMS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ],
      data: afmsDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion AFMS Table


  //#region exhaustfans Table
  generateExhaustFansTable(exhaustFansData: CostReviewExhaustFans): void {
    let exhaustFansDataRows: IData[] = [];
    if (exhaustFansData?.isExhaustFansApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_EXHAUST_FANS'), exhaustFansData.numberOfExhaustFans, exhaustFansDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER'), exhaustFansData.manufacturer, exhaustFansDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_FURNISHED_BY'), exhaustFansData.unitsControlsFurnishedBy, exhaustFansDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_INSTALLED_BY'), exhaustFansData.unitsControlsInstalledBy, exhaustFansDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_WIRED_BY'), exhaustFansData.unitsControlsWiredBy, exhaustFansDataRows, 'SIEMENS_OTHER');
    }
    else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, exhaustFansDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_EXHAUST_FANS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ],
      data: exhaustFansDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion exhaustfans Table

  //#region humidifiers Table

  generateHumidifiersTable(humidifiersData: CostReviewHumidifiers): void {
    let humidifiersDataRows: IData[] = [];
    if (humidifiersData?.isHumidifiersApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_HUMIDIFIERS'), humidifiersData.numberOfHumidifires, humidifiersDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER'), humidifiersData.manufacturer, humidifiersDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_FURNISHED_BY'), humidifiersData.unitsControlsFurnishedBy, humidifiersDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_INSTALLED_BY'), humidifiersData.unitsControlsInstalledBy, humidifiersDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_WIRED_BY'), humidifiersData.unitsControlsWiredBy, humidifiersDataRows, 'SIEMENS_OTHER');
    }
    else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, humidifiersDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_HUMIDIFIERS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ],
      data: humidifiersDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion humidifiers Table

  //#region Air Handlers Table
  generateAirHandlersTable(airHandlersData: CostReviewAirHandlers): void {
    let airHandlersDataRows: IData[] = [];
    if (airHandlersData?.isAirHandlersApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_AIR_HANDLER_UNITS'), airHandlersData.numberOfAirHandlerUnits, airHandlersDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER'), airHandlersData.manufacturer, airHandlersDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_FURNISHED_BY'), airHandlersData.unitsControlsFurnishedBy, airHandlersDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_INSTALLED_BY'), airHandlersData.unitsControlsInstalledBy, airHandlersDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_CONTROLS_WIRED_BY'), airHandlersData.unitsControlsWiredBy, airHandlersDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SPECIAL_SENSORS_REQUIRED'), airHandlersData.specialSensorsRequired, airHandlersDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_FANS'), airHandlersData.numberOfFans, airHandlersDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_DESCRIPTION'), airHandlersData.description, airHandlersDataRows, CONSTANTS.EMPTY_STRING);
    }
    else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, airHandlersDataRows);
    }

    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_AIR_HANDLERS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        {
          data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          alignment: CONSTANTS.LEFT_ALIGNMENT
        }
      ],
      data: airHandlersDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Air Handlers Table

  //#region Electrical Wiring Table
  generateElectricalWiringTable(electricalWiringData: CostReviewElectricalWiring, wiringInterlocksData: CostReviewWiringInterlocks): void {
    let electricalWiringDataRows: IData[] = [];
    if (electricalWiringData?.isElectricalWiringApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_ELECTRICAL_SUBCONTRACTOR'), electricalWiringData.electricalSubcontractor, electricalWiringDataRows, CONSTANTS.EMPTY_STRING);
      if (electricalWiringData.amountBudgeted !== undefined && electricalWiringData.amountBudgeted !== null) {
        electricalWiringDataRows.push(this.createRowWithValuePrefix(this.translate.instant('COST_REVIEW_AMOUNT_BUDGETED'), electricalWiringData.amountBudgeted));
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_IF_SUB_50K_3BIDS_SECURED'), electricalWiringData.threeBidsSecured, electricalWiringDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_REQUESTED_QUOTES_FROM'), electricalWiringData.requestedQuotesFrom, electricalWiringDataRows, CONSTANTS.EMPTY_STRING);
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_FPM_INVOLVED'), electricalWiringData.fpmInvolved, electricalWiringDataRows, 'YES_NO');
        if (electricalWiringData.quoteVariance !== undefined && electricalWiringData.quoteVariance !== null) {
          electricalWiringDataRows.push(this.createRowWithValuePrefix(this.translate.instant('COST_REVIEW_QUOTE_VARIANCE'), electricalWiringData.quoteVariance));
        }
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_RECEIVED_BINDING_QUOTE'), electricalWiringData.receivedBindingQuote, electricalWiringDataRows, 'YES_NO');
        if (electricalWiringData.contractValue !== undefined && electricalWiringData.contractValue !== null) {
          electricalWiringDataRows.push(this.createRowWithValuePrefix(this.translate.instant('COST_REVIEW_CONTRACT_VALUE'), electricalWiringData.contractValue));
        }
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_WIRING_TYPE'), electricalWiringData.wiringType, electricalWiringDataRows, 'ELECTRICALWIRING_WIRING_TYPE');
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SUB_IS_BAU_CERTIFIED'), electricalWiringData.subIsBauCertified, electricalWiringDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SUB_HAS_PROJECT_SPECIFICATIONS'), electricalWiringData.subHasProjectSpecifications, electricalWiringDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SUB_HAS_CAPACITY_TO_COMPLETE_WORK'), electricalWiringData.subHasCapacityToCompleteWork, electricalWiringDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SUB_CARRIES_BOND'), electricalWiringData.subCarriesBond, electricalWiringDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SUB_CARRIES_BOND_REASON'), electricalWiringData.subCarriesBondReason, electricalWiringDataRows);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_ALL_CONDUIT_REQUIRED'), electricalWiringData.allConduitRequired, electricalWiringDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MINIMUM_CONDUIT_SIZE'), electricalWiringData.minimumConduitSize, electricalWiringDataRows, 'MINIMUM_CONDUIT_SIZE');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SAFETIES_FURNISHED_BY'), electricalWiringData.safetiesFurnishedBy, electricalWiringDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_BOILER_CHILLER_WIRING_DETAILED'), wiringInterlocksData.boilerChillerWiringDetailed, electricalWiringDataRows, 'YES_NO');
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_FLOW_SWITCHES_WIRED_BY'), wiringInterlocksData.flowSwitchesWiredBy, electricalWiringDataRows, 'SIEMENS_OTHER');
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_CHEMICAL_FEED_SYSTEM_WIRING_REQUIRED'), wiringInterlocksData.chemicalFeedSystemWiringRequired, electricalWiringDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SUMP_LEVEL_CONTROLLER_WIRING_REQUIRED'), wiringInterlocksData.sumpLevelControllerWiringRequired, electricalWiringDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_OTHER'), wiringInterlocksData.other, electricalWiringDataRows);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SPLIT_SYSTEM_DX_WIRING_REQUIRED'), wiringInterlocksData.splitSystemDxWiringRequired, electricalWiringDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_EXHAUST_FAN_WIRING'), wiringInterlocksData.exhaustFanWiring, electricalWiringDataRows, 'YES_NO');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_EXHAUST_FAN_DAMPER_INTERLOCKS'), wiringInterlocksData.exhaustFanDamperInterlocks, electricalWiringDataRows, 'YES_NO');
      wiringInterlocksData.coolingTowerWiring.forEach((arrayValue: number) => {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_COOLING_TOWER_WIRING'), arrayValue, electricalWiringDataRows, 'COOLING_TOWER_WIRING');
      });
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_PANEL_POWER_WIRED_BY'), wiringInterlocksData.panelPowerWiredBy, electricalWiringDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SMOKE_DETECTORS_INSTALLED_BY'), wiringInterlocksData.smokeDetectorsInstalledBy, electricalWiringDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SMOKE_DETECTORS_FURNISHED_BY'), wiringInterlocksData.smokeDetectorsFurnishedBy, electricalWiringDataRows, 'SIEMENS_OTHER');
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_120_VOLT_INTERLOCKS'), wiringInterlocksData.interlocks120Volt, electricalWiringDataRows, 'YES_NO');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_SMOKE_DETECTORS_LOCATED_BY'), wiringInterlocksData.smokeDetectorsLocatedBy, electricalWiringDataRows, 'SIEMENS_OTHER');
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SIEMENS_PANELS_POWERED_BY'), wiringInterlocksData.siemensPanelsPoweredBy, electricalWiringDataRows, 'SIEMENS_OTHER');
    }
    else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, electricalWiringDataRows);
    }
    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('COST_REVIEW_ELECTRICAL_WIRING'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: electricalWiringDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Electrical Wiring Table

  //#region Dampers Table
  generateDampersTable(dampersData: CostReviewDampers): void {
    let dampersDataRows: IData[] = [];
    if (dampersData?.isDampersApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_RECEIVED_BINDING_QUOTE'), dampersData.receivedBindingQuote, dampersDataRows, 'YES_NO');
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_DAMPER_QUOTE_AMOUNT'), dampersData.damperQuoteAmount, dampersDataRows, CONSTANTS.EMPTY_STRING);
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_DAMPERS'), dampersData.numberOfDampers, dampersDataRows, CONSTANTS.EMPTY_STRING);
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_PRODUCT_NAME_MODEL_NUMBER'), dampersData.manufacturer, dampersDataRows, CONSTANTS.EMPTY_STRING);
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_ACTUATORS_FURNISHED_BY'), dampersData.actuatorsFurnishedBy, dampersDataRows, 'SIEMENS_OTHER');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_ACTUATORS_INSTALLED_BY'), dampersData.actuatorsInstalledBy, dampersDataRows, 'SIEMENS_OTHER');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_ACTUATORS_WIRED_BY'), dampersData.actuatorsWiredBy, dampersDataRows, 'SIEMENS_OTHER');
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SPECIAL_APPLICATIONS_CLOSE_OFF_TEMP_SPEED'), dampersData.specialApplications, dampersDataRows, CONSTANTS.EMPTY_STRING);
    } else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, dampersDataRows);
    }
    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('COST_REVIEW_DAMPERS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: dampersDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Dampers Table

  //#region Smoke Dampers Table
  generateSmokeDampersTable(smokeDampersData: CostReviewSmokeDampers): void {
    let smokeDampersDataRows: IData[] = [];
    if (smokeDampersData?.isSmokeDampersApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_RECEIVED_BINDING_QUOTE'), smokeDampersData.receivedBindingQuote, smokeDampersDataRows, CONSTANTS.EMPTY_STRING);
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_SMOKE_DAMPER_QUOTE_AMOUNT'), smokeDampersData.smokeDamperQuoteAmount, smokeDampersDataRows, CONSTANTS.EMPTY_STRING);
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_NUMBER_OF_SMOKE_DAMPERS'), smokeDampersData.numberOfDampers, smokeDampersDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER_PRODUCT_NAME_MODEL_NUMBER'), smokeDampersData.manufacturer, smokeDampersDataRows, CONSTANTS.EMPTY_STRING);
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_ACTUATORS_FURNISHED_BY'), smokeDampersData.actuatorsFurnishedBy, smokeDampersDataRows, 'SIEMENS_OTHER');
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_ACTUATORS_WIRED_BY'), smokeDampersData.actuatorsWiredBy, smokeDampersDataRows, 'SIEMENS_OTHER');
      }
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SMOKE_DAMPER_END_SWITCH_MONITORING'), smokeDampersData.smokeDamperEndSwitchMonitoring, smokeDampersDataRows, 'YES_NO');
      if (this.estimateType === EstimateType.EstimateGreater150K) {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_IF_YES_WIRING_BY'), smokeDampersData.ifYesWiring, smokeDampersDataRows, 'HARDWIRED_SOFTWARE');
      }
    } else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, smokeDampersDataRows);
    }
    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('COST_REVIEW_SMOKE_DAMPERS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: smokeDampersDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Smoke Dampers Table

  //#region Metering Table
  generateMeteringTable(meteringData: CostReviewMetering): void {
    let meteringDataRows: IData[] = [];
    if (meteringData?.isMeteringApplicable) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_MANUFACTURER'), meteringData.manufacturer, meteringDataRows, CONSTANTS.EMPTY_STRING);
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_INSTALLATION'), meteringData.installation, meteringDataRows, 'HARDWIRED_INTEGRATION');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_METERS_FURNISHED_BY'), meteringData.unitsFurnishedBy, meteringDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_METERS_INSTALLED_BY'), meteringData.unitsInstalledBy, meteringDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_METERS_WIRED_BY'), meteringData.unitsWiredBy, meteringDataRows, 'SIEMENS_OTHER');
    }
    else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, meteringDataRows);
    }
    this.checklistTableData.push({
      title: [
        { data: this.translate.instant('COST_REVIEW_METERING'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: meteringDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Metering Table

  //#region Integration Table
  generateIntegrationTable(integrationData: CostReviewIntegration): void {
    let integrationDataRows: IData[] = [];
    if (integrationData?.isIntegrationApplicatble) {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SYSTEMS_TO_BE_INTEGRATED'), integrationData.systemsThatRequireIntegration, integrationDataRows, CONSTANTS.EMPTY_STRING);
      integrationData.integrationProtocol.forEach((element: number) => {
        this.createRowOneValueData(this.translate.instant('COST_REVIEW_INTEGRATION_PROTOCOL'), element, integrationDataRows, '"INTEGRATION_INTEGRATION_PROTOCOL');
      });
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_FURNISHED_BY'), integrationData.unitsFurnishedBy, integrationDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_INSTALLED_BY'), integrationData.unitsInstalledBy, integrationDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_UNITS_WIRED_BY'), integrationData.unitsWiredBy, integrationDataRows, 'SIEMENS_OTHER');
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_GRAPHICS_REQUIRED'), integrationData.graphicsRequired, integrationDataRows, 'YES_NO');
    }
    else {
      this.createRowOneValueData(this.translate.instant('COST_REVIEW_SECTION_NOT_APPLICABLE'), CONSTANTS.EMPTY_STRING, integrationDataRows);
    }
    this.checklistTableData.push({
      title: [
        {
          data: this.translate.instant('COST_REVIEW_INTEGRATIONS'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT
        },
        { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: CONSTANTS.LEFT_ALIGNMENT }
      ],
      data: integrationDataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });
  }
  //#endregion Integration Table

  //#endregion


}
